/* eslint-disable */

import { regenerateSemanticClips } from '@/adapters/clips';
import { useGetUserAssignedSegments } from '@/adapters/speakers';
import { useVideCut } from '@/adapters/users';
import WorkbenchTranscript from '@/components/Videoplayer/WorkbenchTranscript';
import { UserContext } from '@/contexts/UserContext';
import { usePlaybackPosition, WorkbenchContext } from '@/contexts/WorkbenchContext';
import { useLastSegmentEndTime } from '@/hooks/useLastSegmentEndTime';
import { useRegions } from '@/hooks/useRegions';
import { formatAxisTime, reFormatAxisTime } from '@/lib/general-utils';
import { TestSegmentProps } from '@/types';
import { TranscriptItem as TranscriptItemModel } from '@/types/transcript';
import { ApiFragment, ExtRegionParams } from '@/types/workbench';
import { toHHMMSS } from '@/utils';
import { useAuth0 } from '@auth0/auth0-react';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
// import checkboxIcon from '@/assets/icons/checkbox.svg';
import fullScreenIcon from '@/assets/icons/full-screen.svg';
import searchIcon from '@/assets/icons/search.svg';
// import uncheckboxIcon from '@/assets/icons/uncheckbox.svg';
import backwardIcon from '@/assets/icons/backward.svg';
import forwardIcon from '@/assets/icons/farward.svg';
import pauseIcon from '@/assets/icons/pause.svg';
import playIcon from '@/assets/icons/play.svg';
import resizeHandlerIcon from '@/assets/icons/resize-handler.svg';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Input,
  InputAdornment,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import {
  ChangeEvent,
  FC,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { Region } from 'wavesurfer.js/dist/plugins/regions';
import ClipGrid from '../Waveform/ClipGrid';
import MediaPlayerControls from './MediaPlayerControls';
import SegmentsClipsLane from './SegmentsClipsLane';
import SimpleHlsPlayer from './SimpleHlsPlayer';
import styles from './videoplayer.module.scss';
import Waveform, { ExtRegion } from './Waveform';
// import DefaultButton from '../common/DefaultButton';
import {
  useCreateFragment,
  useDisableFragment,
  useGetFragments,
  useProduceFragment,
  useUpdateFragment,
} from '@/adapters/workbentch';
import PopoutWindow from '@/components/PopoutWindow/PopoutWindow';
import Loading from '../Loading';

export const RegionDefaultColor = 'rgba(255, 255, 255, 0.1)';
export const RegionSelectedColor = 'rgba(53, 193, 118, 0.25)';
export const RegionProcessedColor = 'rgba(136, 136, 136, 0.2)';
export const RegionProcessedSelectedColor = 'rgba(53, 193, 118, 0.15)';
declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    part?: string;
  }

  interface SVGAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    part?: string;
  }
}

export interface AVDisplayProps {
  videoSrc: string;
  cutUrl: string;
  onReady(data: AVComponent): void;
  children?: ReactNode;
  onRegionsDeleted?(regions: ExtRegion[]): void;
  transcriptItems: TranscriptItemModel[];
  refetchProject(): void;
  isCustomizeMode?: boolean;
  /**
   * Seconds to add to local clip-creation timestamps before they're
   * persisted to streamcontroller's `fragments` table. The speaker view
   * runs on a 0-based segment-local timeline, but the row stored in PG
   * must use the full-stream timeline so editor and speaker views agree
   * on the same clip's absolute position. Default 0 (editor mode).
   */
  clipCreateTimeOffsetSec?: number;
  /** Called when the user selects a segment/clip in the timeline (for Brand Kit export popup) */
  onSelectedSegmentChange?(
    segment: {
      id: string;
      name: string;
      type?: 'segment' | 'topic' | 'clip';
    } | null,
  ): void;
  /** Called when the user clicks a block in the Processed segments/clips lane (opens export modal) */
  onSegmentClipCardClick?: () => void;
}

export interface AVComponent {
  video: HTMLVideoElement;
  audioRef: WaveSurfer;
  onControlEvent?(data: string): void;
  scrollToRegion(id: string): void;
  onRegionsLoaded(regionParams: ExtRegionParams[]): void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = (props: TabPanelProps): JSX.Element => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            p: 1,
            flex: 1,
            minHeight: 0,
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number): { id: string; 'aria-controls': string } => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

const AVDisplay: FC<AVDisplayProps> = (props) => {
  const videoRef: MutableRefObject<HTMLVideoElement | null> = useRef(null);
  const [waveSurfer, setWaveSurfer] = useState<WaveSurfer | null>(null);
  const [audioReady, setAudioReady] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [wsRegions, _setWsRegions] = useState<ExtRegion[]>([]);
  const wsRegionsRef = useRef<ExtRegion[]>(wsRegions);
  // const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [segments, setSegments] = useState<TestSegmentProps[]>([]);
  const [topics, setTopics] = useState<TestSegmentProps[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { mutateAsync: videoCut } = useVideCut();
  const { projectId } = useParams<{ projectId: string }>();
  const lastSegmentEndTimeMs = useLastSegmentEndTime();
  const [tabValue, setTabValue] = useState(0);
  const [clips, setClips] = useState<TestSegmentProps[]>([]); // Added state for clips
  // const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);

  const [isTranscriptPoppedOut, setIsTranscriptPoppedOut] = useState(false);
  const [isVideoPoppedOut, setIsVideoPoppedOut] = useState(false);
  const [isSegmentsPoppedOut, setIsSegmentsPoppedOut] = useState(false);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const [isPipWarningOpen, setIsPipWarningOpen] = useState(false);
  const [segmentCreatedSnackbarOpen, setSegmentCreatedSnackbarOpen] = useState(false);
  const [isNewClipInProgress, setIsNewClipInProgress] = useState(false);
  const [isRegeneratingClips, setIsRegeneratingClips] = useState(false);
  // Initialize layout from localStorage so transcript, columns, and waveform (timeline) section restore saved customization on first paint
  const [transcriptHeight, setTranscriptHeight] = useState(() => {
    const key = projectId ? `eos-workbench-layout-${projectId}` : null;
    if (!key || typeof window === 'undefined') return 220;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return 220;
      const parsed = JSON.parse(raw) as { transcriptHeight?: number };
      if (typeof parsed.transcriptHeight === 'number') return parsed.transcriptHeight;
    } catch {
      // ignore
    }
    return 220;
  });
  const [leftColumnWidth, setLeftColumnWidth] = useState(() => {
    const key = projectId ? `eos-workbench-layout-${projectId}` : null;
    if (!key || typeof window === 'undefined') return 70;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return 70;
      const parsed = JSON.parse(raw) as { leftColumnWidth?: number };
      if (typeof parsed.leftColumnWidth === 'number') return parsed.leftColumnWidth;
    } catch {
      // ignore
    }
    return 70;
  });
  const [timelineHeightPx, setTimelineHeightPx] = useState(() => {
    const key = projectId ? `eos-workbench-layout-${projectId}` : null;
    if (!key || typeof window === 'undefined') return 300;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return 300;
      const parsed = JSON.parse(raw) as { timelineHeightPx?: number };
      if (typeof parsed.timelineHeightPx === 'number') return parsed.timelineHeightPx;
    } catch {
      // ignore
    }
    return 300;
  });
  const leftColumnRef = useRef<HTMLDivElement | null>(null);
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const waveformContainerRef = useRef<HTMLDivElement | null>(null);
  const [zoomLevel, setZoomLevel] = useState(10);
  const [waveformScrollContainer, setWaveformScrollContainer] = useState<HTMLElement | null>(null);
  const lastCreatedFragmentIdRef = useRef<number | null>(null);
  const fixedInvalidSegmentIdsRef = useRef<Set<string>>(new Set());
  const [footerStartInput, setFooterStartInput] = useState('');
  const [footerEndInput, setFooterEndInput] = useState('');
  const layoutStorageKey = projectId ? `eos-workbench-layout-${projectId}` : null;
  const prevIsCustomizeModeRef = useRef<boolean>(Boolean(props.isCustomizeMode));
  /** Ref holding latest layout dimensions so we persist the exact values on Save (avoids effect closure staleness) */
  const lastLayoutRef = useRef({ transcriptHeight, leftColumnWidth, timelineHeightPx });
  /** Suppress waveform 'interaction' callback during programmatic seek to avoid setCurrentPosition loop */
  const isProgrammaticSeekRef = useRef(false);
  const lastProgrammaticSeekAtRef = useRef(0);

  // Incremented on every segment/clip/topic time click so the transcript can
  // reliably scroll + highlight without being overwritten by video timeupdate.
  const [seekTrigger, setSeekTrigger] = useState<{ id: number; timeSec: number } | undefined>();
  const seekTriggerId = useRef(0);

  // Narrow viewport: stack columns when width <= 1100 so segments/clips get full width
  const [isNarrowViewport, setIsNarrowViewport] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 1100;
  });
  const isPortraitMode =
    isNarrowViewport &&
    typeof window !== 'undefined' &&
    window.matchMedia('(orientation: portrait)').matches;

  const { userState } = useContext(UserContext);
  const queryClient = useQueryClient();
  // Speakers don't have a Topics tab. If saved layout state or any other
  // path lands them on tabValue === 1, bounce them back to Segments.
  useEffect(() => {
    if (userState?.role === 'user' && tabValue === 1) {
      setTabValue(0);
    }
  }, [userState?.role, tabValue]);
  const { user } = useAuth0();
  const { currentPosition, setCurrentPosition } = usePlaybackPosition();
  const {
    workerId,
    regionParams,
    selectedRegions,
    setFragments,
    isVideoPaused,
    setIsVideoPaused,
    regionsVisible,
  } = useContext(WorkbenchContext);
  const { handleRegionUpdated, setSelectedRegions } = useRegions();
  const { mutateAsync: disableFragments } = useDisableFragment(workerId);
  const fragmentsSpeakerFilter = userState?.role === 'user' && user?.sub ? user.sub : undefined;
  const { isLoading: fragmentsLoading } = useGetFragments(workerId, fragmentsSpeakerFilter);
  const { fragments: allFragments } = useContext(WorkbenchContext);
  const { mutateAsync: produceFragments } = useProduceFragment(workerId);
  const { mutateAsync: updateFragment, isLoading: isUpdatingFragment } =
    useUpdateFragment(workerId);
  const { mutateAsync: createFragment, isLoading: isCreatingFragment } =
    useCreateFragment(workerId);

  const fragments = useMemo(() => allFragments.filter((f) => !f.isDisabled), [allFragments]);

  // Filter regions for waveform by tab: Segments (0), Topics (1), Clips (2)
  const waveformRegionParams = useMemo(() => {
    if (tabValue === 0) {
      return regionParams.filter((r) => r.fragment.type === 'segment');
    }
    if (tabValue === 1) {
      return regionParams.filter((r) => r.fragment.type === 'topic');
    }
    return regionParams.filter((r) => r.fragment.type === 'clip');
  }, [regionParams, tabValue]);

  /** Selected segment for footer (Start/End display and Update Clip): from waveform selection or lane selection */
  const selectedSegmentForFooter = useMemo(() => {
    if (selectedRegions.length > 0) {
      return selectedRegions[0];
    }
    if (selectedSegmentId) {
      return waveformRegionParams.find((r) => r.id === selectedSegmentId) ?? null;
    }
    return null;
  }, [selectedRegions, selectedSegmentId, waveformRegionParams]);

  const setWsRegions = (data: ExtRegion[]): void => {
    _setWsRegions((): ExtRegion[] => {
      const regdata = data;
      wsRegionsRef.current = regdata;
      return regdata;
    });
  };
  const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
  };
  const searchPlaceholder =
    tabValue === 0 ? 'Search segments...' : tabValue === 1 ? 'Search topics...' : 'Search clips...';

  const processedClips = useMemo(() => {
    if (!fragmentsLoading && fragments && fragments.length > 0) {
      return (
        fragments
          .filter((fragment) => fragment.type === 'clip')
          .map((fragment, f) => ({
            id: `${fragment.id}`,
            startTime: fragment.timeStartAdjusted / 100, // Convert to seconds
            endTime:
              (fragment.timeEndAdjusted ?? fragment.timeEnd ?? fragment.timeStartAdjusted) / 100, // Convert to seconds, handle null
            fileName: fragment.fragmentName,
            status: fragment.renderStatus,
            editable: false,
            formErrors: {
              isCreated: false,
              fileNameError: false,
            },
            type: fragment.type,
          }))
          .sort((a, b) => a.startTime - b.startTime) || []
      );
    }
    return [];
  }, [fragments, fragmentsLoading]);

  useEffect(() => {
    setClips((prev) =>
      processedClips.map((pc) => {
        const existing = prev.find((c) => c.id === pc.id);
        return { ...pc, edited: existing?.edited ?? false };
      }),
    );
  }, [processedClips]);

  /** Sync footer Start/End inputs when selected segment changes */
  useEffect(() => {
    if (selectedSegmentForFooter) {
      const startSec = selectedSegmentForFooter.start;
      const endSec = selectedSegmentForFooter.end ?? startSec;
      // Ensure we never display start > end; use min/max for display
      const displayStart = Math.min(startSec, endSec);
      const displayEnd = Math.max(startSec, endSec);
      setFooterStartInput(formatAxisTime(displayStart));
      setFooterEndInput(formatAxisTime(displayEnd));
    } else {
      setFooterStartInput('');
      setFooterEndInput('');
    }
  }, [selectedSegmentForFooter]);

  /** Notify parent (Workbench) when selected segment/topic/clip changes so Export panel shows correct name and preview */
  useEffect(() => {
    if (!props.onSelectedSegmentChange) return;
    if (selectedSegmentForFooter) {
      props.onSelectedSegmentChange({
        id: selectedSegmentForFooter.id ?? '',
        name: selectedSegmentForFooter.fragment.fragmentName ?? '',
        type: selectedSegmentForFooter.fragment.type as 'segment' | 'topic' | 'clip',
      });
    } else {
      props.onSelectedSegmentChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSegmentForFooter]);

  /** Brand Kit popup notification disabled: clicking a region in the waveform should NOT open it */

  /**
   * Sync wavesurfer position and auto-scroll waveform when context position changes
   * Scrolls the waveform container to keep the current time position visible
   * If source is video-playing, scrolls to position time at start; otherwise centers the time
   * Only scrolls if the time position is not already visible in the viewport
   */
  useEffect(() => {
    if (!waveSurfer || currentPosition === undefined) return;

    // Update wavesurfer position without triggering events (suppress interaction callback)
    lastProgrammaticSeekAtRef.current = Date.now();
    isProgrammaticSeekRef.current = true;
    waveSurfer.setTime(currentPosition.timeSec);
    setTimeout(() => {
      isProgrammaticSeekRef.current = false;
    }, 150);

    // Auto-scroll waveform to keep current time visible
    const waveformWrapper = waveSurfer.getWrapper();

    const duration = waveSurfer.getDuration();
    if (!duration) return;

    // The scroll container is the parent of the wrapper
    const scrollContainer = waveformWrapper.parentElement;
    if (!scrollContainer) return;

    // Calculate pixel position of current time
    const scrollWidth = waveformWrapper.scrollWidth;
    const timePositionPx = (currentPosition.timeSec / duration) * scrollWidth;

    const containerScrollLeft = scrollContainer.scrollLeft;
    const containerWidth = scrollContainer.clientWidth;
    const containerScrollRight = containerScrollLeft + containerWidth;

    // Check if time position is already fully visible
    const isFullyVisible =
      timePositionPx >= containerScrollLeft && timePositionPx <= containerScrollRight;

    if (!isFullyVisible) {
      const isVideoPlaying = currentPosition.source === 'video-playing';

      if (isVideoPlaying) {
        // Position time at the start when video is playing
        scrollContainer.scrollLeft = timePositionPx;
      } else {
        // Center the time when navigating via other sources
        scrollContainer.scrollLeft = timePositionPx - containerWidth / 2;
      }
    }
  }, [waveSurfer, currentPosition]);

  const SEEK_AMOUNT_SEC = 10;

  // Listen for orientation and window size changes for responsive layout
  // Debounced to prevent rapid re-renders causing flickering
  useEffect(() => {
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

    const checkViewport = (): void => {
      setIsNarrowViewport(window.innerWidth <= 1100);
    };

    const mediaQuery = window.matchMedia('(orientation: portrait)');

    // Orientation change should be immediate
    const handleOrientationChange = (): void => checkViewport();

    // Debounce resize events to prevent flickering
    const handleResize = (): void => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(checkViewport, 150);
    };

    mediaQuery.addEventListener('change', handleOrientationChange);
    window.addEventListener('resize', handleResize);

    return () => {
      mediaQuery.removeEventListener('change', handleOrientationChange);
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, []);

  const handleScrollToRegion = useCallback(
    (id: string): void => {
      if (!waveSurfer) return;
      const region = wsRegionsRef.current.find((r) => r.id === id);
      if (!region) return;
      waveSurfer.setScrollTime(region.start - 50);
    },
    [waveSurfer],
  );

  const handleRegionsSelected = useCallback((regions: ExtRegionParams[]): void => {
    const regionIds = new Set(regions.map((region) => region.id));
    const _wsRegions = wsRegionsRef.current;
    for (const region of _wsRegions) {
      region.selected = regionIds.has(region.id);
      const isProcessed = region.fragment.renderStatus === 'SCHEDULED';
      const canEdit = !region.fragment.isLocked;
      let colorOption: string;
      if (isProcessed) {
        colorOption = region.selected ? RegionProcessedSelectedColor : RegionProcessedColor;
      } else {
        colorOption = region.selected ? RegionSelectedColor : RegionDefaultColor;
      }
      region.setOptions({
        start: region.start,
        color: colorOption,
        resize: canEdit,
        resizeStart: canEdit,
        resizeEnd: canEdit,
        drag: canEdit,
      });
    }
  }, []);

  // Track selected region IDs so we can distinguish a genuine new selection
  // from a data-only refresh (e.g. after a handle resize updates fragments,
  // reloadRegions rebuilds selectedRegions with the same IDs).
  const prevSelectedRegionIdsRef = useRef('');

  // When true, the next selectedRegions effect should update visuals only
  // (skip scroll + seek) because the position was already set by another
  // mechanism (e.g. handlePlayheadSeek from a ClipGrid card click).
  const skipNextRegionSeekRef = useRef(false);

  // Sync selectedSegmentId → waveform region selection so that clicking a
  // segment/clip/topic card in ClipGrid shows grab handles immediately.
  // This directly applies selection styling on the waveform region DOM
  // elements AND updates selectedRegions state for consistency.
  useEffect(() => {
    if (!selectedSegmentId) return;

    // Check if this region is already selected — avoid redundant updates
    const alreadySelected =
      selectedRegions.length === 1 && selectedRegions[0]?.id === selectedSegmentId;
    if (alreadySelected) return;

    // 1. Directly apply selection styling on waveform region DOM elements
    //    so handles appear immediately (without waiting for state round-trip).
    const wsRegions = wsRegionsRef.current;
    for (const wsRegion of wsRegions) {
      const isTarget = wsRegion.id === selectedSegmentId;
      wsRegion.selected = isTarget;
      const isProcessed = wsRegion.fragment?.renderStatus === 'SCHEDULED';
      const canEdit = !wsRegion.fragment?.isLocked;
      wsRegion.setOptions({
        start: wsRegion.start,
        color: isTarget
          ? isProcessed
            ? RegionProcessedSelectedColor
            : RegionSelectedColor
          : isProcessed
          ? RegionProcessedColor
          : RegionDefaultColor,
        resize: canEdit,
        resizeStart: canEdit,
        resizeEnd: canEdit,
        drag: canEdit,
      });
    }

    // 2. Also update selectedRegions state for consistency (footer, etc.)
    const region = waveformRegionParams.find((r) => r.id === selectedSegmentId);
    if (region) {
      skipNextRegionSeekRef.current = true;
      setSelectedRegions([region]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSegmentId, waveformRegionParams, setSelectedRegions]);

  useEffect(() => {
    if (selectedRegions.length === 0) {
      prevSelectedRegionIdsRef.current = '';
      // Deselect all waveform regions visually (hide handles, reset colors)
      handleRegionsSelected([]);
      return;
    }
    const last = selectedRegions[selectedRegions.length - 1];
    if (!last) return;
    if (!last.id) return;

    // Always update visual styling (colors, resize handles)
    handleRegionsSelected(selectedRegions);

    // Only scroll waveform to start and seek playhead when the SELECTION
    // actually changed (different IDs), not when the same regions were
    // refreshed with updated data (e.g. after a handle drag).
    const currentIds = selectedRegions.map((r) => r.id).join(',');
    if (currentIds !== prevSelectedRegionIdsRef.current) {
      prevSelectedRegionIdsRef.current = currentIds;
      // Skip scroll + seek when the selection was driven by a ClipGrid card
      // click — handlePlayheadSeek already positioned the playhead.
      if (skipNextRegionSeekRef.current) {
        skipNextRegionSeekRef.current = false;
      } else {
        handleScrollToRegion(last.id);
        setCurrentPosition(last.start, 'segment-click');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegions, handleRegionsSelected, handleScrollToRegion, setCurrentPosition]);

  /** Escape key clears selection so "New Clip" shows */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setSelectedRegions([]);
        setSelectedSegmentId(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setSelectedRegions]);

  // Video ready state is now handled by SimpleHlsPlayer
  useEffect(() => {
    setVideoReady(true);
  }, []);

  useEffect(() => {
    // Allow AVComponent to be ready when audio is ready, even if video isn't
    if (!audioReady) {
      return;
    }
    if (waveSurfer === null) {
      console.error('Audio Ref is null');
      return;
    }

    props.onReady({
      video: videoRef.current || ({} as HTMLVideoElement), // Provide fallback for video
      audioRef: waveSurfer,
      scrollToRegion: handleScrollToRegion,
      onRegionsLoaded: () => null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioReady, videoReady, waveSurfer, handleScrollToRegion]);

  const processedSegments = useMemo(() => {
    if (!fragmentsLoading && fragments && fragments.length > 0) {
      return (
        fragments
          .filter((fragment) => fragment.type === 'segment')
          .map((fragment, f) => {
            // Calculate end time, handling null values
            const endTime =
              fragment.timeEndAdjusted !== null
                ? fragment.timeEndAdjusted / 100
                : fragment.timeEnd !== null
                ? fragment.timeEnd / 100
                : lastSegmentEndTimeMs
                ? lastSegmentEndTimeMs / 1000
                : fragment.timeStartAdjusted / 100 + 10;

            return {
              id: `${fragment.id}`,
              startTime: fragment.timeStartAdjusted / 100,
              endTime,
              fileName: fragment.fragmentName || `Segment ${f + 1}`,
              status: fragment.renderStatus,
              editable: fragment.renderStatus !== 'SCHEDULED',
              speakerName: fragment.speakerName,
              speakerLabel: fragment.speakerLabel,
              formErrors: {
                isCreated: false,
                fileNameError: false,
              },
            };
          })
          .sort((a, b) => a.startTime - b.startTime) || []
      );
    }
    return [];
  }, [fragments, fragmentsLoading, lastSegmentEndTimeMs]);

  const processedTopics = useMemo(() => {
    if (!fragmentsLoading && fragments && fragments.length > 0) {
      return (
        fragments
          .filter((fragment) => fragment.type === 'topic')
          .map((fragment, f) => {
            const endTime =
              fragment.timeEndAdjusted !== null
                ? fragment.timeEndAdjusted / 100
                : fragment.timeEnd !== null
                ? fragment.timeEnd / 100
                : lastSegmentEndTimeMs
                ? lastSegmentEndTimeMs / 1000
                : fragment.timeStartAdjusted / 100 + 10;

            return {
              id: `${fragment.id}`,
              startTime: fragment.timeStartAdjusted / 100,
              endTime,
              fileName: fragment.fragmentName || `Topic ${f + 1}`,
              status: fragment.renderStatus,
              editable: fragment.renderStatus !== 'SCHEDULED',
              speakerName: fragment.speakerName,
              speakerLabel: fragment.speakerLabel,
              formErrors: {
                isCreated: false,
                fileNameError: false,
              },
            };
          })
          .sort((a, b) => a.startTime - b.startTime) || []
      );
    }
    return [];
  }, [fragments, fragmentsLoading, lastSegmentEndTimeMs]);

  useEffect(() => {
    setTopics((prev) =>
      processedTopics.map((pt) => {
        const existing = prev.find((t) => t.id === pt.id);
        return { ...pt, edited: existing?.edited ?? false };
      }),
    );
  }, [processedTopics]);

  useEffect(() => {
    if (processedSegments.length >= 0) {
      setSegments((prev) => {
        const existingSegments = new Map(prev.map((segment) => [segment.id, segment]));

        return processedSegments.map((newSegment) => {
          const existingSegment = existingSegments.get(newSegment.id);
          if (existingSegment) {
            return {
              ...existingSegment,
              startTime: newSegment.startTime,
              endTime: newSegment.endTime,
              speakerName: newSegment.speakerName,
              speakerLabel: newSegment.speakerLabel,
              fileName: newSegment.fileName,
              status: newSegment.status,
              editable: newSegment.editable,
            };
          }
          return newSegment;
        });
      });
    }

    if (!waveSurfer) {
      return;
    }
    waveSurfer.on('load', () => {
      // Audio loaded — no action needed here
    });
    const rp = waveSurfer.getActivePlugins()[0] as RegionsPlugin;
    const currentWsRegions = wsRegionsRef.current;
    const wsRegionsById = currentWsRegions.reduce<{ [key: string]: ExtRegion }>((acc, region) => {
      acc[region.id] = region;
      return acc;
    }, {});

    const _wsRegions = waveformRegionParams.map((regionParam: ExtRegionParams) => {
      // console.log('Region Param', regionParam);
      if (regionParam.id && wsRegionsById[regionParam.id]) {
        const wsRegion = wsRegionsById[regionParam.id];
        // console.log(regionParam.id, wsRegion);

        // Check if resize/drag properties have changed - if so, need to recreate the region
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        const currentResize = (wsRegion as any).options?.resize as boolean | undefined;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        const currentDrag = (wsRegion as any).options?.drag as boolean | undefined;
        const needsRecreate =
          (currentResize !== regionParam.resize && currentResize !== undefined) ||
          (currentDrag !== regionParam.drag && currentDrag !== undefined);

        if (needsRecreate) {
          // Remove old region and create new one with updated properties
          // The 'region-created' event will fire and reattach all event listeners
          wsRegion.remove();
          delete wsRegionsById[regionParam.id];
          const newRegion = rp.addRegion(regionParam) as ExtRegion;
          newRegion.regionParams = regionParam;
          newRegion.fragment = regionParam.fragment;
          newRegion.selected = regionParam.selected;
          newRegion.locked = regionParam.locked;
          return newRegion;
        }

        if (wsRegion.element) {
          // Only call setOptions when meaningful data changed (start, end,
          // resize, drag). Skip when only the selection/color changed —
          // selection styling is handled by the selectedRegions effect via
          // handleRegionsSelected. Without this guard, setOptions with
          // default color would undo the selection styling that
          // handleRegionsSelected just applied (it runs earlier in the
          // effect queue).
          const dataChanged =
            Math.abs(wsRegion.start - regionParam.start) > 0.001 ||
            Math.abs(wsRegion.end - (regionParam.end ?? wsRegion.end)) > 0.001 ||
            wsRegion.locked !== regionParam.locked;
          if (dataChanged) {
            wsRegion.setOptions(regionParam);
            // Only sync the selected property when we also update visuals
            // via setOptions. Otherwise, overwriting selected = false would
            // desync the JS property from the DOM state, breaking the
            // rpRegionClicked deselect loop which checks r.selected.
            wsRegion.selected = regionParam.selected;
          }
        }
        wsRegion.regionParams = regionParam;
        wsRegion.fragment = regionParam.fragment;
        wsRegion.locked = regionParam.locked;
        delete wsRegionsById[regionParam.id];
        return wsRegion;
      } else {
        const newRegion = rp.addRegion(regionParam) as ExtRegion;
        newRegion.regionParams = regionParam;
        newRegion.fragment = regionParam.fragment;
        newRegion.selected = regionParam.selected;
        newRegion.locked = regionParam.locked;
        return newRegion;
      }
    });

    Object.keys(wsRegionsById).forEach((key) => {
      wsRegionsById[key].remove();
    });
    setWsRegions([..._wsRegions]);
  }, [waveformRegionParams, lastSegmentEndTimeMs, fragmentsLoading, waveSurfer]);

  /**
   * Handles segment updates from waveform region drag/resize
   * @param region - The updated region
   * @param updatedSide - Which side of the region was adjusted ('start', 'end', or 'both')
   */
  const handleUpdateSegment = useCallback(
    (region: ExtRegion, updatedSide?: 'start' | 'end' | 'both'): void => {
      handleRegionUpdated && handleRegionUpdated(region, updatedSide);
      const updated = {
        startTime: region.start,
        endTime: region.end,
        edited: true,
      };
      setSegments((prev) => prev.map((s) => (s.id === region.id ? { ...s, ...updated } : s)));
      if (region.fragment.type === 'clip') {
        setClips((prev) => prev.map((c) => (c.id === region.id ? { ...c, ...updated } : c)));
      }
    },
    [handleRegionUpdated],
  );

  /**
   * Use a ref for handleUpdateSegment so the plugin-level listener always
   * calls the latest version (the effect below runs only once).
   */
  const handleUpdateSegmentRef = useRef(handleUpdateSegment);
  handleUpdateSegmentRef.current = handleUpdateSegment;

  useEffect(() => {
    if (!waveSurfer) return;
    const rp = waveSurfer.getActivePlugins()[0] as RegionsPlugin;

    let regionLastClicked: Region | null = null;
    const rpRegionClicked = (region: ExtRegion | null, e: MouseEvent): void => {
      if (!region) return;
      e.preventDefault();
      e.stopPropagation();

      // If the clicked region is already selected, seek to the click
      // position within the region instead of jumping to region.start.
      // This lets users scrub inside a selected fragment.
      if (region.selected) {
        const wrapper = waveSurfer?.getWrapper();
        if (wrapper) {
          const rect = wrapper.getBoundingClientRect();
          // rect.left already accounts for scroll (goes negative when
          // scrolled), so no need to add scrollLeft separately.
          const clickOffsetPx = e.clientX - rect.left;
          const clickRatio = clickOffsetPx / wrapper.scrollWidth;
          const duration = waveSurfer?.getDuration() ?? 0;
          const clickTime = Math.max(0, Math.min(duration, clickRatio * duration));
          setCurrentPosition(clickTime, 'waveform-click');
          if (videoRef.current && Number.isFinite(clickTime)) {
            videoRef.current.currentTime = clickTime;
          }
          waveSurfer?.setTime(clickTime);
        }
        return; // keep selection, skip deselect/reselect logic
      }

      if (region.start > 0) {
        setCurrentPosition(region.start, 'segment-click');
      }
      const lastRegionValid = regionLastClicked && regionLastClicked.element;
      const _wsRegions = wsRegionsRef.current;

      if (regionLastClicked && lastRegionValid) {
        _wsRegions.forEach((r) => {
          if (r.selected) {
            r.selected = false;
            const isProcessed = r.fragment.renderStatus === 'SCHEDULED';
            const canEdit = !r.fragment.isLocked;
            r.setOptions({
              start: r.start,
              color: isProcessed ? RegionProcessedColor : RegionDefaultColor,
              resize: canEdit,
              resizeStart: canEdit,
              resizeEnd: canEdit,
              drag: canEdit,
            });
          }
        });
      }
      if (e.shiftKey) {
        if (regionLastClicked) {
          const _rlc = regionLastClicked;
          const selectedRegions = _wsRegions.reduce<ExtRegion[]>((acc, _current) => {
            if (_current.start >= _rlc.start && _current.start <= region.start) {
              return [...acc, _current];
            }
            return acc;
          }, []);
          selectedRegions.forEach((r: ExtRegion) => {
            r.selected = true;
            const isProcessed = r.fragment.renderStatus === 'SCHEDULED';
            const canEdit = !r.fragment.isLocked;
            r.setOptions({
              start: r.start,
              color: isProcessed ? RegionProcessedSelectedColor : RegionSelectedColor,
              resize: canEdit,
              resizeStart: canEdit,
              resizeEnd: canEdit,
              drag: canEdit,
            });
          });
          const regionParams = selectedRegions.map((r) => r.regionParams);
          setSelectedRegions(regionParams);
          setSelectedSegmentId(region.id);
        }
      } else {
        region.selected = true;
        const isProcessed = region.fragment.renderStatus === 'SCHEDULED';
        const canEdit = !region.fragment.isLocked;
        region.setOptions({
          start: region.start,
          color: isProcessed ? RegionProcessedSelectedColor : RegionSelectedColor,
          resize: canEdit,
          resizeStart: canEdit,
          resizeEnd: canEdit,
          drag: canEdit,
        });
        regionLastClicked = region;
        setSelectedRegions([region.regionParams]);
        // Sync selectedSegmentId so the selectedSegmentId effect doesn't
        // re-select a stale segment and overwrite this waveform selection.
        setSelectedSegmentId(region.id);
      }
    };

    /**
     * Use PLUGIN-LEVEL events instead of per-region handlers via region-created.
     *
     * The previous approach relied on rp.on('region-created', onRegionCreated)
     * to attach click/update/update-end listeners to each region. However, the
     * useEffect that creates regions (waveformRegionParams effect) runs BEFORE
     * this useEffect, so the region-created listener was not yet registered when
     * regions were first created — meaning no regions received click or resize
     * handlers, making selection and handle resizing impossible.
     *
     * Plugin-level events (region-clicked, region-update, region-updated) are
     * forwarded by WaveSurfer's saveRegion() for every region regardless of
     * creation order, so they work for all regions once registered.
     */
    const updateSideMap = new Map<string, 'start' | 'end'>();

    rp.on('region-clicked', (region: Region, e: MouseEvent) => {
      rpRegionClicked(region as ExtRegion, e);
    });

    rp.on('region-update', (region: Region, side?: 'start' | 'end') => {
      if (!side) return;
      updateSideMap.set(region.id, side);
      if (side === 'start') {
        setCurrentPosition(region.start, 'waveform-segment-resize');
      } else {
        setCurrentPosition(region.end, 'waveform-segment-resize');
      }
    });

    rp.on('region-updated', (region: Region) => {
      const extRegion = region as ExtRegion;
      const lastSide = updateSideMap.get(region.id);
      handleUpdateSegmentRef.current(extRegion, lastSide);
      updateSideMap.delete(region.id);
    });

    rp.on('region-removed', (region) => {
      const wsRegion = wsRegions.filter((r) => r.id !== region.id);
      setWsRegions(wsRegion);
    });
    return () => {
      rp.unAll();
    };
  }, [waveSurfer]);

  useEffect(() => {
    if (videoElement) {
      // const observer = new ResizeObserver((entries) => {
      //   for (const entry of entries) {
      //     window.requestAnimationFrame((): void | undefined => {
      //       if (videoContainerRef.current) {
      //         videoContainerRef.current.style.height = `${entry.contentRect.height}px`;
      //       }
      //     });
      //   }
      // });
      // if (!isVideoPoppedOut) {
      //   observer.observe(videoElement);
      //   return () => {
      //     observer.disconnect();
      //   };
      // }
    }
  }, [isVideoPoppedOut, videoElement]);

  /**
   * Detects when browser's Picture-in-Picture is activated and shows a warning
   * suggesting the use of the custom popout feature instead
   */
  useEffect(() => {
    if (!videoElement) return;

    const handleEnterPip = (): void => {
      setIsPipWarningOpen(true);
    };

    videoElement.addEventListener('enterpictureinpicture', handleEnterPip);

    return () => {
      videoElement.removeEventListener('enterpictureinpicture', handleEnterPip);
    };
  }, [videoElement]);

  const handleVideoPlayerReady = useCallback((player: HTMLVideoElement): void => {
    videoRef.current = player;
    setVideoElement(player);
  }, []);

  // Stable callbacks for popout window close handlers
  const handleTranscriptPopoutClose = useCallback(() => {
    setIsTranscriptPoppedOut(false);
  }, []);

  const handleVideoPopoutClose = useCallback(() => {
    setIsVideoPoppedOut(false);
  }, []);

  const handleSegmentsPopoutClose = useCallback(() => {
    setIsSegmentsPoppedOut(false);
  }, []);

  /**
   * Closes popout windows when AVDisplay unmounts
   * Resets popout states to trigger PopoutWindow cleanup
   */
  useEffect(() => {
    return () => {
      setIsTranscriptPoppedOut(false);
      setIsVideoPoppedOut(false);
      setIsSegmentsPoppedOut(false);
    };
  }, []);

  const handleWaveformComponentLoad = (wavesurfer: WaveSurfer): void => {
    setWaveSurfer(wavesurfer);
    const scrollEl = wavesurfer.getWrapper().parentElement;
    if (scrollEl) setWaveformScrollContainer(scrollEl);
    // so regions are reloaded
    setWsRegions([]);
    setFragments((fragments) => [...fragments]);

    setAudioReady(true);

    /**
     * Updates position when user clicks on waveform
     * Triggered by single click interactions. Skip when seek is programmatic (segment click, sync effect).
     * Use both ref and timestamp guard - waveform may emit 'interaction' async after setTime().
     */
    const handleWaveformInteraction = (value: number): void => {
      if (isProgrammaticSeekRef.current) return;
      if (Date.now() - lastProgrammaticSeekAtRef.current < 150) return;
      setCurrentPosition(value, 'waveform-click');
    };

    wavesurfer.on('interaction', handleWaveformInteraction);
  };

  /**
   * Handles vertical resize of videoContainer when video is popped out
   * Allows user to drag the resizer bar below the transcript to adjust the entire section's height
   */

  const handlePlayheadSeek = (id: string | undefined, seekStart?: boolean): void => {
    // Try to find in segments, then clips, then topics
    const segment =
      segments.find((seg) => seg.id === id) ||
      clips.find((clip) => clip.id === id) ||
      topics.find((topic) => topic.id === id);
    if (!segment) {
      return;
    }
    const timeSec = seekStart ? segment.startTime : segment.endTime;
    // Seek video and waveform immediately so timeline and transcript update to that point
    if (videoElement && Number.isFinite(timeSec)) {
      videoElement.currentTime = timeSec;
    }
    lastProgrammaticSeekAtRef.current = Date.now();
    isProgrammaticSeekRef.current = true;
    waveSurfer?.setTime(timeSec);
    setCurrentPosition(timeSec, 'segment-click');
    // Fire the seek trigger so the transcript scrolls + highlights reliably,
    // even if video timeupdate overwrites the source in the same React batch.
    seekTriggerId.current += 1;
    setSeekTrigger({ id: seekTriggerId.current, timeSec });
    setTimeout(() => {
      isProgrammaticSeekRef.current = false;
    }, 150);
  };

  const deleteSingleSegment = (id: string): void => {
    if (!id) {
      id = selectedRegions[0]?.id as string;
    }
    if (!id) return;
    setSegments((prev) => prev.filter((seg) => seg.id !== id));
    setFragments((prev) => prev.filter((seg) => seg.id !== +id));
    disableFragments([+id]).catch(console.error);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const createSingleSegment = (_id: string): void => {
    // Segment creation is handled by waveform region interaction in workbench mode
  };

  /**
   * Create a new segment from transcript text selection
   * Creates a fragment with the specified start and end times from the selected transcript text
   * The fragment is created in the backend and added to the local state
   */
  const handleCreateSegmentFromSelection = useCallback(
    async (startTimeSec: number, endTimeSec: number): Promise<void> => {
      if (!workerId) {
        console.warn('Cannot create segment: worker not connected yet.');
        throw new Error('Worker not connected. Please wait for the workbench to finish loading.');
      }
      setIsNewClipInProgress(true);
      try {
        // Ensure start < end; swap if invalid
        let [validStart, validEnd] = [startTimeSec, endTimeSec];
        if (validStart >= validEnd) {
          [validStart, validEnd] = [validEnd, validStart];
        }
        // For speakers, the transcript timeline is 0-based against their
        // segment, so we add the segment's start (in seconds) before the
        // payload hits PG — that way editors viewing the same project see
        // the clip at the correct absolute position on the full stream.
        const offset = props.clipCreateTimeOffsetSec ?? 0;
        // Clips are stored in seconds in the DB
        const timeStartSec = Math.round(validStart + offset);
        const timeEndSec = Math.round(validEnd + offset);

        const clipFragments = allFragments.filter((frag) => frag.type === 'clip');
        // Use max(sequenceNumber)+1 to allow fragments with same start time (unique constraint is on workerId+sequenceNumber+type)
        const maxSeq = clipFragments.length
          ? Math.max(...clipFragments.map((f) => f.sequenceNumber ?? 0))
          : 0;
        const sequenceNumber = maxSeq + 1;

        // Create payload without id so the backend can auto-generate the primary key.
        // For speakers (role === 'user'), tag the clip with their Auth0 sub so the
        // backend can filter the listing to clips they own.
        const createPayload = {
          workerId: workerId,
          graduationId: null,
          studentId: null,
          studentSeatNumber: null,
          renderStatusLastUpdated: null as unknown as string,
          sequenceNumber: sequenceNumber,
          fragmentName: `Clip ${sequenceNumber}`,
          timeStart: timeStartSec,
          timeEnd: timeEndSec,
          timeStartAdjusted: timeStartSec,
          timeEndAdjusted: timeEndSec,
          isDisabled: false,
          isLocked: false,
          renderStatus: 'NEW' as const,
          type: 'clip' as const,
          ...(userState?.role === 'user' && user?.sub ? { speakerId: user.sub } : {}),
        };

        const createdFragment = await createFragment(createPayload as ApiFragment);

        // Normalize in case backend returns different shape (e.g. missing adjusted times)
        // The API returns clip times in seconds, but internal state stores everything
        // in centiseconds (useFragments converts on load). Convert here to stay consistent.
        const isCreatedClip = (createdFragment.type ?? 'clip') === 'clip';
        const rawStart = createdFragment.timeStartAdjusted ?? createdFragment.timeStart;
        const rawEnd = createdFragment.timeEndAdjusted ?? createdFragment.timeEnd;
        const normalized = {
          ...createdFragment,
          timeStart: isCreatedClip ? createdFragment.timeStart * 100 : createdFragment.timeStart,
          timeEnd:
            createdFragment.timeEnd != null
              ? isCreatedClip
                ? createdFragment.timeEnd * 100
                : createdFragment.timeEnd
              : null,
          timeStartAdjusted: isCreatedClip ? rawStart * 100 : rawStart,
          timeEndAdjusted: rawEnd != null ? (isCreatedClip ? rawEnd * 100 : rawEnd) : null,
          type: createdFragment.type ?? 'clip',
        };

        // Add the new fragment to the context (match useRegions: functional update, dedupe, sort)
        setFragments((prevFragments) => {
          const exists = prevFragments.some((f) => f.id === normalized.id);
          if (exists) return prevFragments;
          return [...prevFragments, normalized].sort(
            (a, b) => (a.timeStartAdjusted ?? a.timeStart) - (b.timeStartAdjusted ?? b.timeStart),
          );
        });

        setSegmentCreatedSnackbarOpen(true);
        lastCreatedFragmentIdRef.current = normalized.id;

        // Switch to the Clips tab so the new clip is immediately visible.
        setTabValue(2);

        void queryClient.invalidateQueries(['fragments', workerId]);

        // Refresh project data so the branding sidebar picks up the new clip
        props.refetchProject();
      } catch (error) {
        console.error('Failed to create clip from selection:', error);
        throw error;
      } finally {
        setIsNewClipInProgress(false);
      }
    },
    [
      allFragments,
      createFragment,
      setFragments,
      workerId,
      props.refetchProject,
      props.clipCreateTimeOffsetSec,
      queryClient,
      userState?.role,
      user?.sub,
    ],
  );

  /** Auto-select newly created clip so footer shows Start/End and "Update Clip", and waveform shows handles */
  useEffect(() => {
    const createdId = lastCreatedFragmentIdRef.current;
    if (createdId == null) return;
    lastCreatedFragmentIdRef.current = null;
    const region = regionParams.find((r) => r.id === String(createdId));
    if (region) {
      setSelectedRegions([region]);
      setSelectedSegmentId(String(createdId));

      // Also visually select the WaveSurfer region so handles appear immediately
      const wsRegion = wsRegionsRef.current.find((r) => r.id === String(createdId));
      if (wsRegion) {
        const isProcessed = wsRegion.fragment?.renderStatus === 'SCHEDULED';
        const canEdit = !wsRegion.fragment?.isLocked;
        wsRegion.selected = true;
        wsRegion.setOptions({
          start: wsRegion.start,
          color: isProcessed ? RegionProcessedSelectedColor : RegionSelectedColor,
          resize: canEdit,
          resizeStart: canEdit,
          resizeEnd: canEdit,
          drag: canEdit,
        });
      }
    }
  }, [regionParams, setSelectedRegions]);

  // const handleSaveAllTopTail = (): void => {
  //   let segmentsToSave = segments.filter(
  //     (segment) => ['Staging', 'NEW'].includes(segment.status) && segment.fileName,
  //   );
  //   if (tabValue === 1) {
  //     segmentsToSave = clips.filter(
  //       (segment) => ['Staging', 'NEW'].includes(segment.status) && segment.fileName,
  //     );
  //   }
  //   if (segmentsToSave.length === 0) {
  //     return;
  //   }
  //   setIsSaving(true);

  //   setSegments((prev) => {
  //     return prev.map((segment) => {
  //       if (segmentsToSave.find((s) => s.id === segment.id)) {
  //         return {
  //           ...segment,
  //           editable: false,
  //           status: 'Processing',
  //         };
  //       }
  //       return segment;
  //     });
  //   });

  //   Promise.all(
  //     segmentsToSave.map((segment) =>
  //       videoCut({
  //         start: toHHMMSS(segment.startTime),
  //         end: toHHMMSS(segment.endTime),
  //         projectId: projectId as string,
  //         segmentName: segment.fileName,
  //         fragmentId: segment.id,
  //       })
  //         .then(() => {
  //           setSegments((prev) => {
  //             return prev.map((seg) => {
  //               if (seg.id === segment.id) {
  //                 return {
  //                   ...seg,
  //                   editable: false,
  //                   status: 'SCHEDULED',
  //                 };
  //               }
  //               return seg;
  //             });
  //           });
  //         })
  //         .catch(() => {
  //           setSegments((prev) => {
  //             return prev.map((seg) => {
  //               if (seg.id === segment.id) {
  //                 return {
  //                   ...seg,
  //                   editable: true,
  //                   status: 'Staging',
  //                 };
  //               }
  //               return seg;
  //             });
  //           });
  //         }),
  //     ),
  //   ).catch((error) => {
  //     // Handle aggregate errors here, e.g. show a notification or update UI
  //     console.error('Error saving one or more segments:', error);
  //     setIsSaving(false);
  //   });
  // };

  const handleSaveTopTail = async (segmentId?: string): Promise<void> => {
    const segment = segments.find((seg) => seg.id === segmentId);
    if (!segment) return;
    if (segment.status === 'Processing') return;
    if (!segment.fileName) return;

    try {
      setSegments((prev) => {
        return prev.map((segment) => {
          if (segment.id === segmentId) {
            return {
              ...segment,
              editable: false,
              status: 'Processing',
            };
          }
          return segment;
        });
      });
      await videoCut({
        start: toHHMMSS(segment.startTime),
        end: toHHMMSS(segment.endTime),
        projectId: projectId as string,
        segmentName: segment.fileName,
        fragmentId: segmentId,
      });
      await produceFragments([parseInt(segmentId as string)]);
      setSegments((prev) => {
        return prev.map((segment) => {
          if (segment.id === segmentId) {
            return {
              ...segment,
              editable: false,
              status: 'SCHEDULED',
            };
          }
          return segment;
        });
      });
    } catch (error) {
      setSegments((prev) => {
        return prev.map((segment) => {
          if (segment.id === segmentId) {
            return {
              ...segment,
              editable: true,
              status: 'NEW',
            };
          }
          return segment;
        });
      });
    }
  };

  const handleSaveClips = async (clipId?: string): Promise<void> => {
    const clip = clips.find((c) => c.id === clipId);
    if (!clip) return;
    if (clip.status === 'Processing') return;
    if (!clip.fileName) return;

    try {
      setClips((prev) => {
        return prev.map((segment) => {
          if (segment.id === clipId) {
            return {
              ...segment,
              editable: false,
              status: 'Processing',
            };
          }
          return segment;
        });
      });
      await videoCut({
        start: toHHMMSS(clip.startTime),
        end: toHHMMSS(clip.endTime),
        projectId: projectId as string,
        segmentName: clip.fileName,
        fragmentId: clipId,
      });
      await produceFragments([parseInt(clipId as string)]);
      setClips((prev) => {
        return prev.map((segment) => {
          if (segment.id === clipId) {
            return {
              ...segment,
              editable: false,
              status: 'SCHEDULED',
            };
          }
          return segment;
        });
      });
    } catch (error) {
      setClips((prev) => {
        return prev.map((segment) => {
          if (segment.id === clipId) {
            return {
              ...segment,
              editable: true,
              status: 'NEW',
            };
          }
          return segment;
        });
      });
    }
  };

  const handleSaveTopics = async (topicId?: string): Promise<void> => {
    const topic = topics.find((t) => t.id === topicId);
    if (!topic) return;
    if (topic.status === 'Processing') return;
    if (!topic.fileName) return;

    try {
      setTopics((prev) =>
        prev.map((t) =>
          t.id === topicId ? { ...t, editable: false, status: 'Processing' as const } : t,
        ),
      );
      await videoCut({
        start: toHHMMSS(topic.startTime),
        end: toHHMMSS(topic.endTime),
        projectId: projectId as string,
        segmentName: topic.fileName,
        fragmentId: topicId,
      });
      await produceFragments([parseInt(topicId as string)]);
      setTopics((prev) =>
        prev.map((t) =>
          t.id === topicId ? { ...t, editable: false, status: 'SCHEDULED' as const } : t,
        ),
      );
    } catch (error) {
      setTopics((prev) =>
        prev.map((t) => (t.id === topicId ? { ...t, editable: true, status: 'NEW' as const } : t)),
      );
    }
  };

  /** Regenerate clips from segments when there are segments but no clips (e.g. first load or clips never produced) */
  const handleRegenerateClips = useCallback(
    async (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      if (isRegeneratingClips || !workerId) {
        if (!workerId) console.error('Regenerate clips failed: workerId is not set');
        return;
      }
      setIsRegeneratingClips(true);
      try {
        await regenerateSemanticClips(workerId);
      } catch (err) {
        console.error('Regenerate clips failed:', err);
      } finally {
        setIsRegeneratingClips(false);
      }
    },
    [workerId, isRegeneratingClips],
  );

  const handleSegmentNameChange = useCallback(
    async (segmentId?: string, nameFromInput?: string): Promise<void> => {
      if (!segmentId) return;
      const fragmentId = Number(segmentId);
      if (Number.isNaN(fragmentId)) return;

      const nameToSave =
        nameFromInput !== undefined && nameFromInput !== null
          ? String(nameFromInput).trim()
          : (() => {
              const segmentFromSegments = segments.find((s) => s.id === segmentId);
              const clipFromClips = clips.find((c) => c.id === segmentId);
              const item = segmentFromSegments ?? clipFromClips;
              return (item?.fileName ?? '').trim();
            })();
      if (nameToSave === '') return;

      const fragment = fragments.find((f) => f.id === fragmentId);
      if (!fragment) return;

      try {
        setIsSaving(true);
        // Convert internal centisecond values back to API units:
        // Clips are stored in seconds in the DB, segments in centiseconds
        const isClip = fragment.type === 'clip';
        const apiTimeStart = isClip ? Math.round(fragment.timeStart / 100) : fragment.timeStart;
        const apiTimeEnd =
          fragment.timeEnd !== null
            ? isClip
              ? Math.round(fragment.timeEnd / 100)
              : fragment.timeEnd
            : null;
        const apiTimeStartAdjusted = isClip
          ? Math.round(fragment.timeStartAdjusted / 100)
          : fragment.timeStartAdjusted;
        const apiTimeEndAdjusted =
          fragment.timeEndAdjusted !== null
            ? isClip
              ? Math.round(fragment.timeEndAdjusted / 100)
              : fragment.timeEndAdjusted
            : null;
        const updatedFragment: ApiFragment = {
          ...fragment,
          fragmentName: nameToSave,
          timeStart: apiTimeStart,
          timeEnd: apiTimeEnd,
          timeStartAdjusted: apiTimeStartAdjusted,
          timeEndAdjusted: apiTimeEndAdjusted,
        };
        await updateFragment(updatedFragment);
        setFragments((prev) =>
          prev.map((f) => (f.id === fragmentId ? { ...f, fragmentName: nameToSave } : f)),
        );
        setSegments((prev) =>
          prev.map((s) => (s.id === segmentId ? { ...s, fileName: nameToSave } : s)),
        );
        setClips((prev) =>
          prev.map((c) => (c.id === segmentId ? { ...c, fileName: nameToSave } : c)),
        );
      } catch (err) {
        console.error('Failed to update segment/clip name', err);
      } finally {
        setIsSaving(false);
      }
    },
    [segments, clips, fragments, updateFragment, setFragments, setSegments, setClips],
  );

  const handleFileNameChange = (
    id: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const value = event.target.value;
    const idStr = String(id);
    setSegments((prev) =>
      prev.map((segment) =>
        String(segment.id) === idStr ? { ...segment, fileName: value } : segment,
      ),
    );
  };

  const handleClipFileNameChange = (
    id: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const value = event.target.value;
    const idStr = String(id);
    setClips((prev) =>
      prev.map((clip) => (String(clip.id) === idStr ? { ...clip, fileName: value } : clip)),
    );
  };

  /**
   * Handles segment updates, preserving null values for unadjusted fields.
   * @param segment - The updated segment
   * @param updatedSide - Which side was adjusted: 'start', 'end', 'both', or undefined (defaults to 'both')
   */
  const handleSegmentUpdated = useCallback(
    (segment: TestSegmentProps, updatedSide?: 'start' | 'end' | 'both'): void => {
      if (!segment.id) return;

      const fragmentId = Number(segment.id);
      if (Number.isNaN(fragmentId)) return;

      const fragment = fragments.find((frag) => frag.id === fragmentId);
      if (!fragment) return;

      const isClip = fragment.type === 'clip';
      const side = updatedSide || 'both';

      // Determine new adjusted values based on which side was updated
      // segment.startTime/endTime are always in seconds (converted from centiseconds in the UI).
      // Internal state stores ALL fragment times in centiseconds, so always multiply by 100.
      let timeStartAdjusted: number;
      let timeEndAdjusted: number | null;

      if (side === 'start' || side === 'both') {
        timeStartAdjusted = Math.round(segment.startTime * 100);
      } else {
        // Preserve original value when start wasn't adjusted
        timeStartAdjusted = fragment.timeStartAdjusted;
      }

      if (side === 'end' || side === 'both') {
        timeEndAdjusted = Math.round(segment.endTime * 100);
      } else {
        // Preserve original value (including null) when end wasn't adjusted
        timeEndAdjusted = fragment.timeEndAdjusted;
      }

      // Convert internal centisecond values back to API units:
      // Clips are stored in seconds in the DB, segments in centiseconds
      const apiTimeStartAdjusted = isClip ? Math.round(timeStartAdjusted / 100) : timeStartAdjusted;
      const apiTimeEndAdjusted =
        timeEndAdjusted !== null
          ? isClip
            ? Math.round(timeEndAdjusted / 100)
            : timeEndAdjusted
          : null;

      const updatedFragment: ApiFragment = {
        ...fragment,
        timeStartAdjusted: apiTimeStartAdjusted,
        timeEndAdjusted: apiTimeEndAdjusted,
        renderStatusLastUpdated: new Date().toISOString(),
      };

      // Update local state in centiseconds (internal unit)
      setFragments((prev) =>
        prev.map((frag) =>
          frag.id === fragmentId
            ? {
                ...frag,
                timeStartAdjusted,
                timeEndAdjusted,
                renderStatusLastUpdated: updatedFragment.renderStatusLastUpdated,
              }
            : frag,
        ),
      );

      // Send to API in the correct DB units (seconds for clips, centiseconds for segments)
      void updateFragment(updatedFragment).catch(console.error);
    },
    [fragments, setFragments, updateFragment],
  );

  /**
   * Handles edits to segment start point from ClipGrid or footer
   */
  const handleEditSegmentStart = ({ segment }: { segment: TestSegmentProps }): void => {
    handleSegmentUpdated(segment, 'start');
    const segType = (segment as TestSegmentProps & { type?: string }).type;
    if (segType === 'clip') {
      const idx = clips.findIndex((c) => c.id === segment.id);
      if (idx >= 0) {
        const next = [...clips];
        next[idx] = segment;
        setClips(next);
      }
    } else if (topics.some((t) => t.id === segment.id)) {
      const idx = topics.findIndex((t) => t.id === segment.id);
      if (idx >= 0) {
        const next = [...topics];
        next[idx] = segment;
        setTopics(next);
      }
    } else {
      const idx = segments.findIndex((s) => s.id === segment.id);
      if (idx >= 0) {
        const next = [...segments];
        next[idx] = segment;
        setSegments(next);
      }
    }
  };

  /**
   * Handles edits to segment end point from ClipGrid or footer
   */
  const handleEditSegmentEnd = ({ segment }: { segment: TestSegmentProps }): void => {
    handleSegmentUpdated(segment, 'end');
    const segType = (segment as TestSegmentProps & { type?: string }).type;
    if (segType === 'clip') {
      const idx = clips.findIndex((c) => c.id === segment.id);
      if (idx >= 0) {
        const next = [...clips];
        next[idx] = segment;
        setClips(next);
      }
    } else if (topics.some((t) => t.id === segment.id)) {
      const idx = topics.findIndex((t) => t.id === segment.id);
      if (idx >= 0) {
        const next = [...topics];
        next[idx] = segment;
        setTopics(next);
      }
    } else {
      const idx = segments.findIndex((s) => s.id === segment.id);
      if (idx >= 0) {
        const next = [...segments];
        next[idx] = segment;
        setSegments(next);
      }
    }
  };

  // Speaker-role access list from the server (authoritative). Populated on
  // login for role=user from the `SpeakerSegmentMapping` table. Only queried
  // when the user is actually a speaker so we don't fire it for editors.
  const { data: assignedSegmentsData } = useGetUserAssignedSegments(
    userState?.role === 'user' ? projectId : undefined,
  );

  // Filter segments for speaker users (role "user"). Match priority:
  //   1. Preferred: `seg.id` (PG fragment id) is in the authoritative list of
  //      assigned `fragmentId`s from SpeakerSegmentMapping. This is the most
  //      reliable link — DDB segment names can drift from PG fragmentName.
  //   2. Backup: `fileName` (segment title) matches one of the assigned
  //      segment names (case/whitespace-insensitive).
  //   3. Final fallback (when mapping is empty): user.name appears as one of
  //      the comma-separated speakers on the segment.
  const filteredSegments = useMemo(() => {
    if (userState?.role !== 'user') {
      return segments;
    }

    const normalizeForMatch = (str: string): string =>
      str.toLowerCase().trim().replace(/\s+/g, ' ');

    const assignedSegments = assignedSegmentsData?.segments ?? [];
    const allowedFragmentIds = new Set<string>(
      assignedSegments.map((s) => s.fragmentId).filter((id): id is string => !!id),
    );
    const allowedNames = new Set<string>(
      assignedSegments.map((s) => (s.name ? normalizeForMatch(s.name) : '')).filter(Boolean),
    );

    if (allowedFragmentIds.size > 0 || allowedNames.size > 0) {
      return segments.filter(
        (seg) =>
          (!!seg.id && allowedFragmentIds.has(seg.id)) ||
          allowedNames.has(normalizeForMatch(seg.fileName ?? '')),
      );
    }

    if (!user?.name) return [];
    const normalizedUserName = normalizeForMatch(user.name);
    return segments.filter((seg) => {
      if (!seg.speakerName) return false;
      return seg.speakerName
        .split(/[;,&]|\s+and\s+/i)
        .map((n) => normalizeForMatch(n))
        .includes(normalizedUserName);
    });
  }, [segments, userState?.role, user?.name, assignedSegmentsData]);

  // Filter by search query (fileName, speaker, time codes)
  const searchFilteredSegments = useMemo(() => {
    if (!searchQuery.trim()) return filteredSegments;
    const q = searchQuery.toLowerCase().trim();
    return filteredSegments.filter((seg) => {
      const fileName = (seg.fileName ?? '').toLowerCase();
      const speakerName = (seg.speakerName ?? '').toLowerCase();
      const speakerLabel = (seg.speakerLabel ?? '').toLowerCase();
      const startStr = formatAxisTime(seg.startTime);
      const endStr = formatAxisTime(seg.endTime);
      return (
        fileName.includes(q) ||
        speakerName.includes(q) ||
        speakerLabel.includes(q) ||
        startStr.includes(q) ||
        endStr.includes(q)
      );
    });
  }, [filteredSegments, searchQuery]);

  const searchFilteredClips = useMemo(() => {
    if (!searchQuery.trim()) return clips;
    const q = searchQuery.toLowerCase().trim();
    return clips.filter((seg) => {
      const fileName = (seg.fileName ?? '').toLowerCase();
      const speakerName = (seg.speakerName ?? '').toLowerCase();
      const speakerLabel = (seg.speakerLabel ?? '').toLowerCase();
      const startStr = formatAxisTime(seg.startTime);
      const endStr = formatAxisTime(seg.endTime);
      return (
        fileName.includes(q) ||
        speakerName.includes(q) ||
        speakerLabel.includes(q) ||
        startStr.includes(q) ||
        endStr.includes(q)
      );
    });
  }, [clips, searchQuery]);

  const searchFilteredTopics = useMemo(() => {
    if (!searchQuery.trim()) return topics;
    const q = searchQuery.toLowerCase().trim();
    return topics.filter((seg) => {
      const fileName = (seg.fileName ?? '').toLowerCase();
      const speakerName = (seg.speakerName ?? '').toLowerCase();
      const speakerLabel = (seg.speakerLabel ?? '').toLowerCase();
      const startStr = formatAxisTime(seg.startTime);
      const endStr = formatAxisTime(seg.endTime);
      return (
        fileName.includes(q) ||
        speakerName.includes(q) ||
        speakerLabel.includes(q) ||
        startStr.includes(q) ||
        endStr.includes(q)
      );
    });
  }, [topics, searchQuery]);

  // Memoized props for virtualized components to prevent unnecessary re-renders
  const segmentsGridProps = useMemo(
    () => ({
      isReady: true,
      segments: searchFilteredSegments,
      setSegments,
      handlePlayheadSeek,
      deleteSingleSegment,
      createSingleSegment,
      saving: isSaving,
      saveTopTail: handleSaveTopTail,
      saveNameChange: handleSegmentNameChange,
      handleFileNameChange,
      editClipStartPoint: handleEditSegmentStart,
      editClipEndPoint: handleEditSegmentEnd,
      selectedSegmentId,
      setSelectedSegmentId,
      userRole: userState?.role,
      onSegmentOrClipCardClick: props.onSegmentClipCardClick,
    }),
    [
      searchFilteredSegments,
      setSegments,
      handlePlayheadSeek,
      deleteSingleSegment,
      createSingleSegment,
      isSaving,
      handleSaveTopTail,
      handleSegmentNameChange,
      handleFileNameChange,
      handleEditSegmentStart,
      handleEditSegmentEnd,
      selectedSegmentId,
      setSelectedSegmentId,
      userState?.role,
      props.onSegmentClipCardClick,
    ],
  );

  const clipsGridProps = useMemo(
    () => ({
      isReady: true,
      segments: searchFilteredClips,
      setSegments,
      handlePlayheadSeek,
      deleteSingleSegment,
      createSingleSegment,
      saving: isSaving,
      saveTopTail: handleSaveClips,
      saveNameChange: handleSegmentNameChange,
      handleFileNameChange: handleClipFileNameChange,
      editClipStartPoint: handleEditSegmentStart,
      editClipEndPoint: handleEditSegmentEnd,
      // Speakers (role === 'user') only ever see their own clips on this tab
      // because the streamcontroller list endpoint filters by speakerId. So
      // it's safe to drop the read-only gate for the Clips tab specifically —
      // segments/topics tabs keep the original userRole and stay read-only.
      userRole: userState?.role === 'user' ? undefined : userState?.role,
      selectedSegmentId,
      setSelectedSegmentId,
      onSegmentOrClipCardClick: props.onSegmentClipCardClick,
    }),
    [
      searchFilteredClips,
      setSegments,
      handlePlayheadSeek,
      deleteSingleSegment,
      createSingleSegment,
      isSaving,
      handleSaveClips,
      handleSegmentNameChange,
      handleClipFileNameChange,
      handleEditSegmentStart,
      handleEditSegmentEnd,
      selectedSegmentId,
      setSelectedSegmentId,
      props.onSegmentClipCardClick,
      userState?.role,
    ],
  );

  const topicsGridProps = useMemo(
    () => ({
      isReady: true,
      segments: searchFilteredTopics,
      setSegments: setTopics,
      handlePlayheadSeek,
      deleteSingleSegment,
      createSingleSegment,
      saving: isSaving,
      saveTopTail: handleSaveTopics,
      saveNameChange: handleSegmentNameChange,
      handleFileNameChange,
      editClipStartPoint: handleEditSegmentStart,
      editClipEndPoint: handleEditSegmentEnd,
      userRole: userState?.role,
      selectedSegmentId,
      setSelectedSegmentId,
      onSegmentOrClipCardClick: props.onSegmentClipCardClick,
    }),
    [
      searchFilteredTopics,
      setTopics,
      handlePlayheadSeek,
      deleteSingleSegment,
      createSingleSegment,
      isSaving,
      handleSaveTopics,
      handleSegmentNameChange,
      handleFileNameChange,
      handleEditSegmentStart,
      handleEditSegmentEnd,
      selectedSegmentId,
      setSelectedSegmentId,
      props.onSegmentClipCardClick,
      userState?.role,
    ],
  );

  // Better loading state that considers both fragments loading and data processing
  const isSegmentsLoading =
    fragmentsLoading || (processedSegments.length === 0 && fragments && fragments.length > 0);
  const isTopicsLoading =
    fragmentsLoading || (processedTopics.length === 0 && fragments && fragments.length > 0);
  const isClipsLoading =
    fragmentsLoading || (processedClips.length === 0 && fragments && fragments.length > 0);
  // When we have segments but no clips yet, show empty state with Regenerate instead of infinite loading
  const hasSegmentsButNoClips =
    !fragmentsLoading &&
    fragments.length > 0 &&
    processedClips.length === 0 &&
    fragments.some((f) => f.type === 'segment');

  // Vertical resize: drag the bar between Transcript and Playback to change transcript height (px)
  const handleVerticalResize = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      e.preventDefault();
      const startY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const startHeight = transcriptHeight;

      // Compute the hard ceiling at drag-start: leftColumn height minus the 14px handle
      // minus the video panel's minimum height (200px) so the video never overflows leftColumn.
      const ROW_HANDLE_PX = 14;
      const VIDEO_MIN_PX = 200;
      const columnHeight = leftColumnRef.current?.clientHeight ?? 820;
      const maxTranscriptHeight = Math.max(120, columnHeight - ROW_HANDLE_PX - VIDEO_MIN_PX);

      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';

      const moveHandler = (moveEvent: MouseEvent | TouchEvent): void => {
        const currentY = 'touches' in moveEvent ? moveEvent.touches[0].clientY : moveEvent.clientY;
        const deltaY = currentY - startY;
        const newHeight = Math.max(120, Math.min(startHeight + deltaY, maxTranscriptHeight));
        lastLayoutRef.current = { ...lastLayoutRef.current, transcriptHeight: newHeight };
        setTranscriptHeight(newHeight);
      };

      const upHandler = (): void => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', upHandler);
        document.removeEventListener('touchmove', moveHandler);
        document.removeEventListener('touchend', upHandler);
      };

      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', upHandler);
      document.addEventListener('touchmove', moveHandler);
      document.addEventListener('touchend', upHandler);
    },
    [transcriptHeight],
  );

  // Horizontal resize: drag the bar between left column and segments to change column split (%)
  const handleHorizontalResize = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      e.preventDefault();
      const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const startWidth = leftColumnWidth;
      const containerWidth = layoutRef.current?.getBoundingClientRect().width || 0;

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const moveHandler = (moveEvent: MouseEvent | TouchEvent): void => {
        const currentX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
        const deltaX = currentX - startX;
        const deltaPercent = (deltaX / containerWidth) * 100;
        const newWidth = Math.min(Math.max(startWidth + deltaPercent, 25), 75);
        lastLayoutRef.current = { ...lastLayoutRef.current, leftColumnWidth: newWidth };
        setLeftColumnWidth(newWidth);
      };

      const upHandler = (): void => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', upHandler);
        document.removeEventListener('touchmove', moveHandler);
        document.removeEventListener('touchend', upHandler);
      };

      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', upHandler);
      document.addEventListener('touchmove', moveHandler, { passive: false });
      document.addEventListener('touchend', upHandler);
    },
    [leftColumnWidth],
  );

  // Timeline resize: drag the bar above the timeline to change its height (px)
  const handleTimelineResize = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const startY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const startHeightPx = timelineHeightPx;

      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';

      const moveHandler = (moveEvent: MouseEvent | TouchEvent): void => {
        const currentY = 'touches' in moveEvent ? moveEvent.touches[0].clientY : moveEvent.clientY;
        const deltaY = startY - currentY; // dragging up increases height, dragging down decreases (can collapse to 0)
        const minHeight = 0; // in customize mode user can drag to bottom to collapse waveform
        const newHeightPx = Math.max(minHeight, Math.min(startHeightPx + deltaY, 700));
        lastLayoutRef.current = { ...lastLayoutRef.current, timelineHeightPx: newHeightPx };
        setTimelineHeightPx(newHeightPx);
      };

      const upHandler = (): void => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', upHandler);
        document.removeEventListener('touchmove', moveHandler);
        document.removeEventListener('touchend', upHandler);
      };

      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', upHandler);
      document.addEventListener('touchmove', moveHandler, { passive: false });
      document.addEventListener('touchend', upHandler);
    },
    [timelineHeightPx],
  );

  // Restore layout preferences (heights/widths) from localStorage for this project
  useEffect(() => {
    if (!layoutStorageKey || typeof window === 'undefined') return;

    try {
      const raw = window.localStorage.getItem(layoutStorageKey);
      if (!raw) return;

      const parsed = JSON.parse(raw) as {
        transcriptHeight?: number;
        leftColumnWidth?: number;
        timelineHeightPx?: number;
      };

      if (typeof parsed.transcriptHeight === 'number') {
        setTranscriptHeight(parsed.transcriptHeight);
      }
      if (typeof parsed.leftColumnWidth === 'number') {
        setLeftColumnWidth(parsed.leftColumnWidth);
      }
      if (typeof parsed.timelineHeightPx === 'number') {
        setTimelineHeightPx(parsed.timelineHeightPx);
      }
    } catch (err) {
      console.warn('[AVDisplay] Failed to restore layout from localStorage', err);
    }
  }, [layoutStorageKey]);

  // Keep ref in sync with current layout so we persist the exact values on Save
  lastLayoutRef.current = { transcriptHeight, leftColumnWidth, timelineHeightPx };

  // Persist layout to localStorage while in customize mode (on every change) so saved state is always up to date
  useEffect(() => {
    if (!layoutStorageKey || typeof window === 'undefined' || !props.isCustomizeMode) return;
    try {
      const payload = {
        transcriptHeight: lastLayoutRef.current.transcriptHeight,
        leftColumnWidth: lastLayoutRef.current.leftColumnWidth,
        timelineHeightPx: lastLayoutRef.current.timelineHeightPx,
      };
      window.localStorage.setItem(layoutStorageKey, JSON.stringify(payload));
    } catch (err) {
      console.warn('[AVDisplay] Failed to persist layout to localStorage', err);
    }
  }, [
    props.isCustomizeMode,
    layoutStorageKey,
    transcriptHeight,
    leftColumnWidth,
    timelineHeightPx,
  ]);

  // When exiting customize mode (Save), persist from ref and keep state in sync so the layout stays after Save
  useEffect(() => {
    const prev = prevIsCustomizeModeRef.current;
    const current = Boolean(props.isCustomizeMode);

    if (layoutStorageKey && typeof window !== 'undefined' && prev && !current) {
      const {
        transcriptHeight: th,
        leftColumnWidth: lcw,
        timelineHeightPx: thp,
      } = lastLayoutRef.current;
      try {
        window.localStorage.setItem(
          layoutStorageKey,
          JSON.stringify({
            transcriptHeight: th,
            leftColumnWidth: lcw,
            timelineHeightPx: thp,
          }),
        );
      } catch (err) {
        console.warn('[AVDisplay] Failed to persist layout to localStorage', err);
      }
      // Apply saved dimensions so the non-customize view shows the same layout (avoids any revert)
      setTranscriptHeight(th);
      setLeftColumnWidth(lcw);
      setTimelineHeightPx(thp);
    }

    prevIsCustomizeModeRef.current = current;
  }, [props.isCustomizeMode, layoutStorageKey]);

  // Force reflow when entering customize mode so gap + timeline resize handle get correct layout on first paint
  useEffect(() => {
    if (!props.isCustomizeMode) return;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (!cancelled && layoutRef.current) void layoutRef.current.offsetHeight;
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [props.isCustomizeMode]);

  // Redraw waveform when its container resizes (e.g. timeline height drag) so canvas fills the new space
  useEffect(() => {
    if (!waveSurfer) return;

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        waveSurfer.zoom(zoomLevel);
      });
    });

    const container = waveformContainerRef.current;
    if (container) {
      resizeObserver.observe(container);
    }

    return () => resizeObserver.disconnect();
  }, [waveSurfer, zoomLevel]);

  // Determine if left column is effectively empty (both transcript and video popped out)
  const isLeftColumnEmpty = isTranscriptPoppedOut && isVideoPoppedOut;

  // Compute grid template columns based on popped out state
  // Returns undefined when narrow so layout stacks (single column) and segments get full width
  const getGridTemplateColumns = (): string | undefined => {
    if (isNarrowViewport) {
      return undefined;
    }

    if (isLeftColumnEmpty && !isSegmentsPoppedOut) {
      return '0 1fr';
    }
    if (isSegmentsPoppedOut && !isLeftColumnEmpty) {
      return '1fr 0';
    }
    if (isLeftColumnEmpty && isSegmentsPoppedOut) {
      return '1fr 0';
    }

    // Customize mode: insert a 28px resize handle column between left and right
    if (props.isCustomizeMode) {
      return `${leftColumnWidth}% 28px 1fr`;
    }

    return `${leftColumnWidth}% 1fr`;
  };

  // Timeline header height (px) — used so waveform area gets explicit height from saved layout (same pattern as transcript panel)
  const TIMELINE_HEADER_HEIGHT_PX = 52;
  const waveformAreaHeightPx = Math.max(0, timelineHeightPx - TIMELINE_HEADER_HEIGHT_PX);

  const handleTimelinePlayPause = useCallback(() => {
    if (!videoElement) return;
    if (videoElement.paused) {
      videoElement.play().catch((_err: unknown) => {
        /* play() rejection ignored */
      });
      setIsVideoPaused(false);
    } else {
      videoElement.pause();
      setIsVideoPaused(true);
    }
  }, [videoElement, setIsVideoPaused]);

  const handleTimelineBackward = useCallback(() => {
    if (!videoElement) return;
    const t = Math.max(0, videoElement.currentTime - SEEK_AMOUNT_SEC);
    videoElement.currentTime = t;
    setCurrentPosition(t, 'video-scrubbing');
  }, [videoElement, setCurrentPosition]);

  const handleTimelineForward = useCallback(() => {
    if (!videoElement) return;
    const dur = videoElement.duration;
    const t =
      Number.isFinite(dur) && dur > 0
        ? Math.min(dur, videoElement.currentTime + SEEK_AMOUNT_SEC)
        : videoElement.currentTime + SEEK_AMOUNT_SEC;
    videoElement.currentTime = Math.max(0, t);
    setCurrentPosition(videoElement.currentTime, 'video-scrubbing');
  }, [videoElement, setCurrentPosition]);

  const handleZoomChange = useCallback(
    (newZoom: number) => {
      setZoomLevel(newZoom);
      waveSurfer?.zoom(newZoom);
    },
    [waveSurfer],
  );

  const handleFit = useCallback(() => {
    if (!waveSurfer) return;
    const container = waveSurfer.getWrapper().parentElement;
    if (!container) return;
    const duration = waveSurfer.getDuration();
    if (!duration) return;
    const pxPerSec = container.clientWidth / duration;
    handleZoomChange(Math.max(1, pxPerSec));
  }, [waveSurfer, handleZoomChange]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  // J (reverse 10s), K (play/pause), L (forward 10s),
  // \ (zoom to fit), +/= (zoom in), -/_ (zoom out), Space (play/pause)
  // All suppressed when focus is in a text input, textarea, select, or contentEditable.
  useEffect(() => {
    const keyListener = (e: KeyboardEvent): void => {
      if (!videoRef.current) return;

      // Skip hotkeys when the user is typing in any text-input-like element
      const target = e.target as HTMLElement;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (target?.isContentEditable) return;

      const player = videoRef.current;

      switch (e.key) {
        // ── Spacebar: toggle play / pause ──
        case ' ':
          e.preventDefault();
          if (player.paused) {
            player.play().catch(console.error);
          } else {
            player.pause();
          }
          break;

        // ── J: reverse 10s ──
        case 'j':
        case 'J': {
          e.preventDefault();
          const tJ = Math.max(0, player.currentTime - SEEK_AMOUNT_SEC);
          player.currentTime = tJ;
          setCurrentPosition(tJ, 'video-scrubbing');
          break;
        }

        // ── K: play / pause ──
        case 'k':
        case 'K':
          e.preventDefault();
          if (!player.paused) {
            player.pause();
            setIsVideoPaused(true);
          } else {
            player.play().catch(console.error);
            setIsVideoPaused(false);
          }
          break;

        // ── L: forward 10s ──
        case 'l':
        case 'L': {
          e.preventDefault();
          const dur = player.duration;
          const tL =
            Number.isFinite(dur) && dur > 0
              ? Math.min(dur, player.currentTime + SEEK_AMOUNT_SEC)
              : player.currentTime + SEEK_AMOUNT_SEC;
          player.currentTime = Math.max(0, tL);
          setCurrentPosition(player.currentTime, 'video-scrubbing');
          break;
        }

        // ── \: zoom to fit (show entire timeline) ──
        case '\\':
          e.preventDefault();
          handleFit();
          break;

        // ── + / =: zoom in ──
        case '+':
        case '=': {
          e.preventDefault();
          const zoomIn = Math.min(100, zoomLevel + 5);
          handleZoomChange(zoomIn);
          break;
        }

        // ── - / _: zoom out ──
        case '-':
        case '_': {
          e.preventDefault();
          const zoomOut = Math.max(1, zoomLevel - 5);
          handleZoomChange(zoomOut);
          break;
        }

        default:
          break;
      }
    };
    window.addEventListener('keydown', keyListener);
    return () => {
      window.removeEventListener('keydown', keyListener);
    };
  }, [setCurrentPosition, setIsVideoPaused, handleFit, handleZoomChange, zoomLevel]);

  const handleNewClip = useCallback(() => {
    if (!videoElement) return;

    // 1. Get current playhead position
    const startTimeSec = videoElement.currentTime;

    // 2. Default duration: 10 seconds, clamped to remaining video
    const defaultDuration = 10;
    const videoDuration = Number.isFinite(videoElement.duration) ? videoElement.duration : 300;
    const endTimeSec = Math.min(startTimeSec + defaultDuration, videoDuration);

    // 3. Trigger fragment creation (creates a segment that appears in SegmentsClipsLane)
    void handleCreateSegmentFromSelection(startTimeSec, endTimeSec);

    // 4. Scroll the waveform to the new clip
    waveSurfer?.setTime(startTimeSec);
  }, [videoElement, handleCreateSegmentFromSelection, waveSurfer]);

  const formattedCurrentTime = toHHMMSS(Math.floor(currentPosition?.timeSec ?? 0));
  const formattedDuration =
    videoElement && Number.isFinite(videoElement.duration)
      ? toHHMMSS(Math.floor(videoElement.duration))
      : '00:00:00';

  const handleFooterStartChange = useCallback((): void => {
    if (!selectedSegmentForFooter || !footerStartInput.match(/^[0-9:]*$/)) return;
    const currentList = tabValue === 0 ? segments : tabValue === 1 ? topics : clips;
    const found = currentList.find((s) => s.id === selectedSegmentForFooter.id);
    if (!found) return;
    const newStart = reFormatAxisTime(footerStartInput);
    if (newStart >= (found.endTime ?? 0)) return;
    handleEditSegmentStart({ segment: { ...found, startTime: newStart } });
  }, [
    selectedSegmentForFooter,
    footerStartInput,
    tabValue,
    segments,
    topics,
    clips,
    handleEditSegmentStart,
  ]);

  const handleFooterEndChange = useCallback((): void => {
    if (!selectedSegmentForFooter || !footerEndInput.match(/^[0-9:]*$/)) return;
    const currentList = tabValue === 0 ? segments : tabValue === 1 ? topics : clips;
    const found = currentList.find((s) => s.id === selectedSegmentForFooter.id);
    if (!found) return;
    const newEnd = reFormatAxisTime(footerEndInput);
    if (newEnd <= (found.startTime ?? 0)) return;
    handleEditSegmentEnd({ segment: { ...found, endTime: newEnd } });
  }, [
    selectedSegmentForFooter,
    footerEndInput,
    tabValue,
    segments,
    topics,
    clips,
    handleEditSegmentEnd,
  ]);

  const isFooterSegmentProcessed = selectedSegmentForFooter?.fragment.renderStatus === 'SCHEDULED';

  /** Auto-correct segments with start > end (persist fix to backend) */
  useEffect(() => {
    if (!selectedSegmentForFooter) return;
    const startSec = selectedSegmentForFooter.start;
    const endSec = selectedSegmentForFooter.end ?? startSec;
    if (startSec <= endSec) return;
    const segId = selectedSegmentForFooter.id;
    if (!segId || fixedInvalidSegmentIdsRef.current.has(segId)) return;
    fixedInvalidSegmentIdsRef.current.add(segId);
    const currentList = tabValue === 0 ? segments : tabValue === 1 ? topics : clips;
    const found = currentList.find((s) => s.id === segId);
    if (!found) return;
    const correctStart = Math.min(startSec, endSec);
    const correctEnd = Math.max(startSec, endSec);
    handleEditSegmentStart({ segment: { ...found, startTime: correctStart } });
    handleEditSegmentEnd({ segment: { ...found, startTime: correctStart, endTime: correctEnd } });
  }, [
    selectedSegmentForFooter,
    tabValue,
    segments,
    topics,
    clips,
    handleEditSegmentStart,
    handleEditSegmentEnd,
  ]);

  // Handle export requests coming from the Workbench Brand Kit popup
  useEffect(() => {
    const handleExportClip = async (event: Event): Promise<void> => {
      const customEvent = event as CustomEvent<{
        segmentName?: string;
        aspectRatio?: string;
        brandOption?: string;
      }>;
      const detail = customEvent.detail || {};
      const exportName = (detail.segmentName || '').trim();
      if (!exportName) return;

      const target = selectedSegmentForFooter;
      if (!target?.id) {
        console.warn('Export clip ignored: no segment selected in timeline.');
        window.dispatchEvent(
          new CustomEvent('eos-export-clip-error', {
            detail: { reason: 'no-segment-selected' },
          }),
        );
        return;
      }

      try {
        await handleSegmentNameChange(target.id, exportName);

        window.dispatchEvent(
          new CustomEvent('eos-export-clip-started', {
            detail: {
              segmentId: target.id,
              segmentName: exportName,
              aspectRatio: detail.aspectRatio,
              brandOption: detail.brandOption,
            },
          }),
        );

        if (tabValue === 2) {
          await handleSaveClips(target.id);
        } else {
          await handleSaveTopTail(target.id);
        }

        const startSec = target.start ?? 0;
        const endSec = target.end ?? startSec;
        window.dispatchEvent(
          new CustomEvent('eos-export-clip-completed', {
            detail: {
              segmentId: target.id,
              segmentName: exportName,
              startTimestamp: startSec,
              endTimestamp: endSec,
            },
          }),
        );
      } catch (error) {
        console.error('Export clip failed:', error);
        window.dispatchEvent(
          new CustomEvent('eos-export-clip-error', {
            detail: { reason: 'export-failed' },
          }),
        );
      }
    };

    const listener = (event: Event): void => {
      void handleExportClip(event);
    };

    window.addEventListener('eos-export-clip', listener as EventListener);
    return () => {
      window.removeEventListener('eos-export-clip', listener as EventListener);
    };
  }, [
    selectedSegmentForFooter,
    tabValue,
    handleSegmentNameChange,
    handleSaveTopTail,
    handleSaveClips,
  ]);

  return (
    <>
      {/* Outer flex-column scroll container */}
      <div
        className={`${styles.viewLayout} ${
          props.isCustomizeMode ? styles.viewLayoutCustomize : ''
        }`}
        ref={layoutRef}
      >
        {/* Top workspace grid: Left column (transcript+video) | [col handle] | Right column (segments) */}
        <div
          className={styles.mainWorkspace}
          style={{ gridTemplateColumns: getGridTemplateColumns() }}
        >
          {/* Left Column - Stacked Transcript + Video */}
          {!isLeftColumnEmpty && (
            <div
              className={`${styles.leftColumn} ${
                props.isCustomizeMode ? styles.leftColumnCustomize : ''
              }`}
              ref={leftColumnRef}
            >
              {/* Transcription Panel */}
              {!isTranscriptPoppedOut && (
                <div
                  className={styles.transcriptionPanel}
                  style={isVideoPoppedOut ? { flex: 1 } : { height: transcriptHeight }}
                >
                  {props.transcriptItems.length > 0 ? (
                    <WorkbenchTranscript
                      transcriptItems={props.transcriptItems}
                      onCreateSegmentFromSelection={handleCreateSegmentFromSelection}
                      createSelectionButtonLabel="New Clip"
                      onPopout={() => setIsTranscriptPoppedOut(true)}
                      seekTrigger={seekTrigger}
                    />
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                      }}
                    >
                      <Loading />
                    </div>
                  )}
                </div>
              )}

              {/* Vertical Resize Handle - between Transcript and Playback, visible in customize mode */}
              {props.isCustomizeMode && !isTranscriptPoppedOut && !isVideoPoppedOut && (
                <div
                  className={styles.rowResizeHandle}
                  onMouseDown={handleVerticalResize}
                  onTouchStart={handleVerticalResize}
                  title="Drag to resize panels"
                >
                  <img src={resizeHandlerIcon} alt="" className={styles.resizeHandlerIconRotated} />
                </div>
              )}

              {/* Video Panel - fixed dimensions below transcript, controls below video */}
              {!isVideoPoppedOut && (
                <div className={styles.videoPanel}>
                  <div className={`${styles.panelHeader} ${styles.videoPanelHeader}`}>
                    <span className={styles.videoPanelTitle}>PLAYBACK</span>
                    <Tooltip title="Pop out video to separate window">
                      <button
                        type="button"
                        className={styles.videoPanelPopoutBtn}
                        onClick={() => setIsVideoPoppedOut(true)}
                        aria-label="Pop out video"
                      >
                        <img src={fullScreenIcon} alt="Pop out" />
                      </button>
                    </Tooltip>
                  </div>
                  <div className={styles.videoContent}>
                    <SimpleHlsPlayer
                      onReady={handleVideoPlayerReady}
                      src={props.videoSrc}
                      showNativeControls={false}
                    />
                  </div>
                  <MediaPlayerControls videoElement={videoElement} />
                </div>
              )}
            </div>
          )}

          {/* Horizontal Resize Handle - visible in customize mode on desktop */}
          {props.isCustomizeMode &&
            !isLeftColumnEmpty &&
            !isSegmentsPoppedOut &&
            !isNarrowViewport && (
              <div
                className={styles.colResizeHandle}
                onMouseDown={handleHorizontalResize}
                onTouchStart={handleHorizontalResize}
                title="Drag to resize columns"
              >
                <img src={resizeHandlerIcon} alt="" className={styles.resizeHandlerIcon} />
              </div>
            )}

          {/* Row Resize Handle - visible in customize mode on mobile between left column and segments */}
          {props.isCustomizeMode && !isLeftColumnEmpty && !isSegmentsPoppedOut && isNarrowViewport && (
            <div className={styles.rowResizeHandle} style={{ gridColumn: '1 / -1' }}>
              <img src={resizeHandlerIcon} alt="" className={styles.resizeHandlerIconRotated} />
            </div>
          )}

          {/* Right Column - Segments/Clips */}
          <div
            className={styles.rightColumn}
            style={
              isLeftColumnEmpty
                ? { gridColumn: '1 / -1' }
                : isNarrowViewport
                ? { gridColumn: '1 / -1' }
                : props.isCustomizeMode && !isSegmentsPoppedOut
                ? { gridColumn: '3' }
                : {}
            }
          >
            <div
              className={`${styles.rightColumnHeader} ${styles.segmentsHeader} ${
                searchExpanded ? styles.segmentsHeaderSearchMode : ''
              }`}
            >
              {searchExpanded ? (
                <div className={styles.searchBarFullWidth}>
                  <Input
                    autoFocus
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={() => {
                      if (!searchQuery.trim()) setSearchExpanded(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setSearchQuery('');
                        setSearchExpanded(false);
                      }
                      if (e.key === ' ') e.stopPropagation();
                    }}
                    placeholder={searchPlaceholder}
                    className={styles.searchExpandInput}
                    disableUnderline
                    startAdornment={
                      <InputAdornment position="start">
                        <img
                          src={searchIcon}
                          alt=""
                          style={{ width: 16, height: 16, opacity: 0.6 }}
                        />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Clear search"
                          onClick={() => {
                            setSearchQuery('');
                            setSearchExpanded(false);
                          }}
                          sx={{ padding: '4px' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </div>
              ) : (
                <>
                  <div className={styles.segmentsTabs}>
                    <button
                      type="button"
                      className={`${styles.segmentsTab} ${
                        tabValue === 0 ? styles.segmentsTabActive : ''
                      }`}
                      onClick={() => setTabValue(0)}
                      aria-label="Segments"
                    >
                      SEGMENTS
                    </button>
                    {userState?.role !== 'user' && (
                      <button
                        type="button"
                        className={`${styles.segmentsTab} ${
                          tabValue === 1 ? styles.segmentsTabActive : ''
                        }`}
                        onClick={() => setTabValue(1)}
                        aria-label="Topics"
                      >
                        TOPICS
                      </button>
                    )}
                    <button
                      type="button"
                      className={`${styles.segmentsTab} ${
                        tabValue === 2 ? styles.segmentsTabActive : ''
                      }`}
                      onClick={() => setTabValue(2)}
                      aria-label="Clips"
                    >
                      CLIPS
                    </button>
                  </div>
                  <div className={styles.segmentsHeaderIcons}>
                    <Tooltip title="Search">
                      <button
                        type="button"
                        className={styles.segmentsIconBtn}
                        aria-label="Search"
                        onClick={() => setSearchExpanded(true)}
                      >
                        <img src={searchIcon} alt="Search" />
                      </button>
                    </Tooltip>
                    {/* <Tooltip title="Select all">
                      <button
                        type="button"
                        className={styles.segmentsIconBtn}
                        aria-label="Select all"
                        aria-pressed={selectAllChecked}
                        onClick={() => setSelectAllChecked((prev) => !prev)}
                      >
                        <img
                          src={selectAllChecked ? checkboxIcon : uncheckboxIcon}
                          alt={selectAllChecked ? 'Select all (checked)' : 'Select all (unchecked)'}
                        />
                      </button>
                    </Tooltip> */}
                    <Tooltip title="More options">
                      <IconButton
                        size="small"
                        className={styles.segmentsIconBtn}
                        aria-label="More"
                        sx={{ padding: '4px', minWidth: 24, minHeight: 24 }}
                      >
                        <MoreVertIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </>
              )}
            </div>
            {!isSegmentsPoppedOut ? (
              <div className={styles.segmentsContainer}>
                <CustomTabPanel value={tabValue} index={0}>
                  {isSegmentsLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                      <Loading />
                    </div>
                  ) : segments.length !== 0 ? (
                    searchFilteredSegments.length === 0 && searchQuery.trim() ? (
                      <div
                        style={{
                          padding: '20px',
                          color: '#888',
                          textAlign: 'center',
                        }}
                      >
                        No segments match your search
                      </div>
                    ) : (
                      <>
                        <ClipGrid {...segmentsGridProps} variant="workbench" />
                        <div
                          className={styles.searchResultsFooter}
                          style={{
                            padding: '12px',
                            textAlign: 'center',
                            color: '#888',
                            fontSize: '13px',
                          }}
                        >
                          {searchQuery.trim()
                            ? `Showing ${searchFilteredSegments.length} result${
                                searchFilteredSegments.length !== 1 ? 's' : ''
                              }`
                            : 'Showing all results'}
                        </div>
                      </>
                    )
                  ) : (
                    <div style={{ padding: '20px', color: '#888', textAlign: 'center' }}>
                      There are no segments loaded
                    </div>
                  )}
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={1}>
                  {isTopicsLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                      <Loading />
                    </div>
                  ) : topics.length === 0 ? (
                    <div
                      style={{
                        padding: '20px',
                        color: '#888',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px',
                      }}
                    >
                      <span>No topics loaded yet.</span>
                      <Button
                        type="button"
                        variant="contained"
                        disabled={isRegeneratingClips}
                        onClick={handleRegenerateClips}
                      >
                        {isRegeneratingClips ? 'Generating…' : 'Regenerate topics'}
                      </Button>
                    </div>
                  ) : searchFilteredTopics.length === 0 && searchQuery.trim() ? (
                    <div
                      style={{
                        padding: '20px',
                        color: '#888',
                        textAlign: 'center',
                      }}
                    >
                      No topics match your search
                    </div>
                  ) : (
                    <>
                      <ClipGrid {...topicsGridProps} variant="workbench" />
                      <div
                        className={styles.searchResultsFooter}
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          color: '#888',
                          fontSize: '13px',
                        }}
                      >
                        {searchQuery.trim()
                          ? `Showing ${searchFilteredTopics.length} result${
                              searchFilteredTopics.length !== 1 ? 's' : ''
                            }`
                          : 'Showing all results'}
                      </div>
                    </>
                  )}
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={2}>
                  {fragmentsLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                      <Loading />
                    </div>
                  ) : clips.length !== 0 ? (
                    searchFilteredClips.length === 0 && searchQuery.trim() ? (
                      <div
                        style={{
                          padding: '20px',
                          color: '#888',
                          textAlign: 'center',
                        }}
                      >
                        No clips match your search
                      </div>
                    ) : (
                      <>
                        <ClipGrid {...clipsGridProps} variant="workbench" />
                        <div
                          className={styles.searchResultsFooter}
                          style={{
                            padding: '12px',
                            textAlign: 'center',
                            color: '#888',
                            fontSize: '13px',
                          }}
                        >
                          {searchQuery.trim()
                            ? `Showing ${searchFilteredClips.length} result${
                                searchFilteredClips.length !== 1 ? 's' : ''
                              }`
                            : 'Showing all results'}
                        </div>
                      </>
                    )
                  ) : (
                    <div style={{ padding: '20px', color: '#888', textAlign: 'center' }}>
                      There are no clips loaded
                    </div>
                  )}
                </CustomTabPanel>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  color: '#888',
                  fontSize: '14px',
                }}
              >
                Segments are in a separate window
              </div>
            )}
          </div>
        </div>
        {/* end mainWorkspace */}

        {/* Timeline Resize Handle — flex item between mainWorkspace and timeline */}
        {props.isCustomizeMode && (
          <div
            className={styles.timelineRowResizeHandle}
            onMouseDown={handleTimelineResize}
            onTouchStart={handleTimelineResize}
            title="Drag to resize timeline; drag to bottom to collapse"
          >
            <img src={resizeHandlerIcon} alt="" className={styles.resizeHandlerIconRotated} />
          </div>
        )}

        {/* Timeline - height from saved layout (same pattern as transcript panel); minHeight 0 so saved height is always applied (not overridden by CSS min-height) */}
        <div
          className={styles.timelineContainer}
          style={{
            height: `${timelineHeightPx}px`,
            flexShrink: 0,
            minHeight: 0,
          }}
        >
          {/* 1. HEADER: Tabs, Controls, and Zoom */}
          <div className={styles.timelineHeader}>
            <div className={styles.timelineHeaderLeft}>
              <span className={styles.timelineTitle}>
                TIMELINE [{tabValue === 0 ? 'SEGMENTS' : tabValue === 1 ? 'TOPICS' : 'CLIPS'}]
              </span>
            </div>

            <div className={styles.timelineClipControls}>
              <span className={styles.timelineControlsLabel}>CLIP CONTROLS</span>
              <button
                type="button"
                className={styles.timelineControlBtn}
                onClick={handleTimelineBackward}
                disabled={!videoElement}
                aria-label="Rewind 10 seconds"
              >
                <img src={backwardIcon} alt="Rewind" style={{ width: 14, height: 14 }} />
              </button>
              <button
                type="button"
                className={styles.timelineControlBtn}
                onClick={handleTimelinePlayPause}
                disabled={!videoElement}
                aria-label={isVideoPaused ? 'Play' : 'Pause'}
              >
                <img
                  src={isVideoPaused ? playIcon : pauseIcon}
                  alt={isVideoPaused ? 'Play' : 'Pause'}
                  style={{ width: 14, height: 14 }}
                />
              </button>
              <button
                type="button"
                className={styles.timelineControlBtn}
                onClick={handleTimelineForward}
                disabled={!videoElement}
                aria-label="Forward 10 seconds"
              >
                <img src={forwardIcon} alt="Forward" style={{ width: 14, height: 14 }} />
              </button>
            </div>

            <div className={styles.timelineZoomControls}>
              <button
                type="button"
                className={styles.timelineZoomBtn}
                onClick={() => handleZoomChange(Math.max(1, zoomLevel - 5))}
                aria-label="Zoom out"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
                  <line
                    x1="4"
                    y1="7"
                    x2="10"
                    y2="7"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <input
                type="range"
                className={styles.timelineZoomSlider}
                min={1}
                max={100}
                value={zoomLevel}
                onChange={(e) => handleZoomChange(Number(e.target.value))}
                aria-label="Zoom level"
              />
              <button
                type="button"
                className={styles.timelineZoomBtn}
                onClick={() => handleZoomChange(Math.min(100, zoomLevel + 5))}
                aria-label="Zoom in"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
                  <line
                    x1="4"
                    y1="7"
                    x2="10"
                    y2="7"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                  <line
                    x1="7"
                    y1="4"
                    x2="7"
                    y2="10"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <button type="button" className={styles.timelineFitBtn} onClick={handleFit}>
                FIT
              </button>
            </div>

            <button
              type="button"
              className={styles.timelineSupportBtn}
              onClick={() => window.dispatchEvent(new CustomEvent('eos-support-open'))}
              aria-label="Open support chat"
              title="Support"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span className={styles.timelineSupportLabel}>Support</span>
            </button>
          </div>

          {/* 2. THE VISUAL LANE & WAVEFORM — height from saved layout (same pattern as transcript panel) */}
          <div
            className={styles.timelineWaveform}
            style={{ height: waveformAreaHeightPx, flexShrink: 0 }}
          >
            {userState?.role !== 'user' && (
              <SegmentsClipsLane
                items={waveformRegionParams}
                durationSec={
                  videoElement && Number.isFinite(videoElement.duration)
                    ? videoElement.duration
                    : waveSurfer?.getDuration() ?? 300
                }
                zoomLevel={zoomLevel}
                waveformScrollContainer={waveformScrollContainer}
                onBlockClick={(id) => {
                  handlePlayheadSeek(id, true);
                  const regionParam = waveformRegionParams.find((r) => r.id === id);
                  if (regionParam) {
                    setSelectedRegions([regionParam]);
                    setSelectedSegmentId(id);
                  }
                }}
                onBackgroundClick={() => {
                  setSelectedRegions([]);
                  setSelectedSegmentId(null);
                }}
              />
            )}
            <div
              className="wavesurfer-container"
              style={{ flex: 1, minHeight: 0 }}
              ref={waveformContainerRef}
              onClick={(e) => {
                const target = e.target as HTMLElement;
                if (!target.closest('[part*="region"]')) {
                  setSelectedRegions([]);
                  setSelectedSegmentId(null);
                }
              }}
            >
              <Waveform
                onComponentLoad={handleWaveformComponentLoad}
                regionsVisible={regionsVisible}
                waveformViewMode={tabValue === 2 ? 'clips' : 'segments'}
              />
            </div>
          </div>

          {/* 3. FOOTER: Time, Segment (when selected), and New Clip / Update Clip Action */}
          {/* <div className={styles.timelineFooter}>
            {!selectedSegmentForFooter && (
              <div className={styles.timelineCurrentTime}>
                <span className={styles.timelineTimeLabel}>CURRENT TIME</span>
                <span className={styles.timelineTimeValue}>{formattedCurrentTime}</span>
              </div>
            )}
            {selectedSegmentForFooter && (
              <>
                <div className={styles.timelineSegmentNum}>
                  {selectedSegmentForFooter.fragment.sequenceNumber ??
                    Math.max(
                      1,
                      waveformRegionParams.findIndex((r) => r.id === selectedSegmentForFooter.id) +
                        1,
                    )}
                </div>
                <span className={styles.timelineSegmentName}>
                  {selectedSegmentForFooter.fragment.fragmentName || (tabValue === 2 ? 'Clip' : tabValue === 1 ? 'Topic' : 'Segment')}
                </span>
                <button
                  type="button"
                  className={styles.timelineClearBtn}
                  onClick={() => {
                    setSelectedRegions([]);
                    setSelectedSegmentId(null);
                  }}
                >
                  CLEAR
                </button>
                <div className={styles.timelineCurrentTime}>
                  <span className={styles.timelineTimeLabel}>START</span>
                  <input
                    type="text"
                    className={styles.timelineTimeInput}
                    value={footerStartInput}
                    onChange={(e) => setFooterStartInput(e.target.value)}
                    onBlur={handleFooterStartChange}
                    onKeyDown={(e) => e.key === 'Enter' && handleFooterStartChange()}
                    disabled={isFooterSegmentProcessed}
                    aria-label="Segment start time"
                  />
                </div>
                <span className={styles.timelineTimeArrow} aria-hidden>
                  &#8594;
                </span>
                <div className={styles.timelineCurrentTime}>
                  <span className={styles.timelineTimeLabel}>FINISH</span>
                  <input
                    type="text"
                    className={styles.timelineTimeInput}
                    value={footerEndInput}
                    onChange={(e) => setFooterEndInput(e.target.value)}
                    onBlur={handleFooterEndChange}
                    onKeyDown={(e) => e.key === 'Enter' && handleFooterEndChange()}
                    disabled={isFooterSegmentProcessed}
                    aria-label="Segment finish time"
                  />
                </div>
              </>
            )}
            <span className={styles.timelineDuration}>{formattedDuration}</span>
            {selectedSegmentForFooter && (
              <button
                type="button"
                className={styles.timelineDeleteBtn}
                onClick={() => {
                  if (selectedSegmentForFooter.id) {
                    deleteSingleSegment(selectedSegmentForFooter.id);
                    setSelectedRegions([]);
                    setSelectedSegmentId(null);
                  }
                }}
                disabled={isFooterSegmentProcessed}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                Delete
              </button>
            )}
            {selectedSegmentForFooter ? (
              <button
                type="button"
                className={styles.timelineNewClipBtn}
                disabled={isUpdatingFragment}
                onClick={() => {
                  handleFooterStartChange();
                  handleFooterEndChange();
                }}
              >
                {isUpdatingFragment ? (
                  <>
                    <span className={styles.timelineBtnSpinner} aria-hidden />
                    Updating...
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    Update Clip
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                className={styles.timelineNewClipBtn}
                disabled={isNewClipInProgress || isCreatingFragment}
                onClick={handleNewClip}
              >
                {isNewClipInProgress || isCreatingFragment ? (
                  <>
                    <span className={styles.timelineBtnSpinner} aria-hidden />
                    Creating...
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    New Clip
                  </>
                )}
              </button>
            )}
          </div> */}
        </div>
      </div>

      {/* Region Portals */}
      {wsRegionsRef.current.map((region, index) => {
        const fragment = region.fragment;
        if (!region.element) return null;
        if (!fragment) return null;
        const finishedStates = new Set<string>(['PUBLISHED', 'ERROR']);
        const isRendering = false;
        const isInProgress = !finishedStates.has(fragment.renderStatus);
        return createPortal(
          <>
            <div part="region-overlay" style={{ pointerEvents: 'none' }}>
              {isRendering && (
                <>
                  {isInProgress && (
                    <div part="region-content">
                      {fragment.fragmentName && <p>{fragment.fragmentName}</p>}
                    </div>
                  )}
                </>
              )}
              <div part="region-footer">
                <div part="region-footer-icons"></div>
              </div>
            </div>
          </>,
          region.element,
          'portal__' + String(index),
        );
      })}

      {/* Popout windows */}
      {isTranscriptPoppedOut && (
        <PopoutWindow
          title="Transcript"
          width={800}
          height={900}
          onClose={handleTranscriptPopoutClose}
        >
          <Box
            style={{
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#fff',
              padding: '20px',
            }}
          >
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
                paddingBottom: 8,
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <h2 style={{ margin: 0 }}>Transcript</h2>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloseFullscreenIcon />}
                onClick={handleTranscriptPopoutClose}
              >
                Return to main window
              </Button>
            </Box>
            {props.transcriptItems.length > 0 ? (
              <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <WorkbenchTranscript
                  transcriptItems={props.transcriptItems}
                  onCreateSegmentFromSelection={handleCreateSegmentFromSelection}
                  createSelectionButtonLabel="New Clip"
                  seekTrigger={seekTrigger}
                />
              </Box>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Loading />
              </div>
            )}
          </Box>
        </PopoutWindow>
      )}

      {isVideoPoppedOut && (
        <PopoutWindow
          title="Video Player"
          width={1200}
          height={800}
          onClose={handleVideoPopoutClose}
        >
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#000',
              position: 'relative',
            }}
          >
            <SimpleHlsPlayer
              onReady={handleVideoPlayerReady}
              src={props.videoSrc}
              showNativeControls
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseFullscreenIcon />}
              onClick={handleVideoPopoutClose}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: '#fff',
                borderColor: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderColor: '#fff',
                },
              }}
            >
              Return to main window
            </Button>
          </Box>
        </PopoutWindow>
      )}

      {/* Segments Popout Window */}
      {isSegmentsPoppedOut && (
        <PopoutWindow
          title="Segments, Topics & Clips"
          width={700}
          height={600}
          onClose={handleSegmentsPopoutClose}
        >
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#fff',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                padding: '8px 16px',
                gap: 8,
              }}
            >
              {searchExpanded ? (
                <div style={{ flex: 1, minWidth: 0 }} className={styles.searchBarFullWidth}>
                  <Input
                    autoFocus
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={() => {
                      if (!searchQuery.trim()) setSearchExpanded(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setSearchQuery('');
                        setSearchExpanded(false);
                      }
                      if (e.key === ' ') e.stopPropagation();
                    }}
                    placeholder={searchPlaceholder}
                    className={styles.searchExpandInput}
                    disableUnderline
                    startAdornment={
                      <InputAdornment position="start">
                        <img
                          src={searchIcon}
                          alt=""
                          style={{ width: 16, height: 16, opacity: 0.6 }}
                        />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Clear search"
                          onClick={() => {
                            setSearchQuery('');
                            setSearchExpanded(false);
                          }}
                          sx={{ padding: '4px' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </div>
              ) : (
                <>
                  <Tabs value={tabValue} onChange={handleTabChange} aria-label="segment tabs">
                    <Tab label="Segments" {...a11yProps(0)} />
                    <Tab label="Topics" {...a11yProps(1)} />
                    <Tab label="Clips" {...a11yProps(2)} />
                  </Tabs>
                  <Tooltip title="Search">
                    <button
                      type="button"
                      className={styles.segmentsIconBtn}
                      aria-label="Search"
                      onClick={() => setSearchExpanded(true)}
                    >
                      <img src={searchIcon} alt="Search" />
                    </button>
                  </Tooltip>
                </>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloseFullscreenIcon />}
                onClick={handleSegmentsPopoutClose}
              >
                Return to main window
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', padding: 1 }}>
              <CustomTabPanel value={tabValue} index={0}>
                {isSegmentsLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <Loading />
                  </div>
                ) : segments.length !== 0 ? (
                  searchFilteredSegments.length === 0 && searchQuery.trim() ? (
                    <div
                      style={{
                        padding: '20px',
                        color: '#888',
                        textAlign: 'center',
                      }}
                    >
                      No segments match your search
                    </div>
                  ) : (
                    <>
                      <ClipGrid {...segmentsGridProps} variant="workbench" />
                      <div
                        className={styles.searchResultsFooter}
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          color: '#888',
                          fontSize: '13px',
                        }}
                      >
                        {searchQuery.trim()
                          ? `Showing ${searchFilteredSegments.length} result${
                              searchFilteredSegments.length !== 1 ? 's' : ''
                            }`
                          : 'Showing all results'}
                      </div>
                    </>
                  )
                ) : (
                  <div style={{ padding: '20px', color: '#888', textAlign: 'center' }}>
                    There are no segments loaded
                  </div>
                )}
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={1}>
                {isTopicsLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <Loading />
                  </div>
                ) : topics.length === 0 ? (
                  <div
                    style={{
                      padding: '20px',
                      color: '#888',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '16px',
                    }}
                  >
                    <span>No topics loaded yet.</span>
                    <Button
                      type="button"
                      variant="contained"
                      disabled={isRegeneratingClips}
                      onClick={handleRegenerateClips}
                    >
                      {isRegeneratingClips ? 'Generating…' : 'Regenerate topics'}
                    </Button>
                  </div>
                ) : searchFilteredTopics.length === 0 && searchQuery.trim() ? (
                  <div
                    style={{
                      padding: '20px',
                      color: '#888',
                      textAlign: 'center',
                    }}
                  >
                    No topics match your search
                  </div>
                ) : (
                  <ClipGrid {...topicsGridProps} variant="workbench" />
                )}
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={2}>
                {fragmentsLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <Loading />
                  </div>
                ) : clips.length !== 0 ? (
                  searchFilteredClips.length === 0 && searchQuery.trim() ? (
                    <div
                      style={{
                        padding: '20px',
                        color: '#888',
                        textAlign: 'center',
                      }}
                    >
                      No clips match your search
                    </div>
                  ) : (
                    <ClipGrid {...clipsGridProps} variant="workbench" />
                  )
                ) : (
                  <div style={{ padding: '20px', color: '#888', textAlign: 'center' }}>
                    There are no clips loaded
                  </div>
                )}
              </CustomTabPanel>
            </Box>
          </Box>
        </PopoutWindow>
      )}

      {/* Warning when browser Picture-in-Picture is used */}
      <Snackbar
        open={isPipWarningOpen}
        autoHideDuration={6000}
        onClose={() => setIsPipWarningOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setIsPipWarningOpen(false)} severity="info" sx={{ width: '100%' }}>
          For the best experience, please use the custom popout button (
          <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle', mx: 0.5 }} />) instead of the
          browser&apos;s Picture-in-Picture feature. The custom popout supports both video and
          transcript windows.
        </Alert>
      </Snackbar>

      {/* Success message when a clip is created from transcript selection */}
      <Snackbar
        open={segmentCreatedSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSegmentCreatedSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSegmentCreatedSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Clip created.
        </Alert>
      </Snackbar>
    </>
  );
};

export default AVDisplay;
